import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  ComputerDesktopIcon,
  CpuChipIcon,
  DevicePhoneMobileIcon,
  PrinterIcon,
  TruckIcon,
  WrenchScrewdriverIcon,
  CubeIcon,
  BuildingOfficeIcon,
  EyeIcon,
  PencilIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  XCircleIcon,
  XMarkIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  BarsArrowUpIcon,
  BarsArrowDownIcon,
  CurrencyDollarIcon,
  CalendarDaysIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  UserIcon,
  ArrowsRightLeftIcon,
  PhotoIcon,
  CameraIcon,
  DocumentIcon
} from '@heroicons/react/24/outline';
import {
  CheckCircleIcon as CheckCircleSolidIcon,
  XCircleIcon as XCircleSolidIcon,
  ClockIcon as ClockSolidIcon,
  UserIcon as UserSolidIcon,
  PlayIcon
} from '@heroicons/react/24/solid';
import { Menu } from '@headlessui/react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import {
  useGetAssetsQuery,
  useCreateAssetMutation,
  useUpdateAssetMutation,
  useAssignAssetMutation,
  useReturnAssetMutation,
  useGetAssetRequestsQuery,
  useApproveAssetRequestMutation,
  useRejectAssetRequestMutation,
  useFulfillAssetRequestMutation,
  useTransferAssetMutation,
  useGetAssetHistoryQuery,
  useScheduleMaintenanceMutation,
  useStartMaintenanceMutation,
  useCompleteMaintenanceMutation,
  useCancelMaintenanceMutation
} from '../../services/assetApi';
import { useGetBranchesQuery } from '../../services/branchApi';
import { useGetEmployeesQuery } from '../../services/employeeApi';
import { Card, Button, Input, Select, Modal, DataTable, LoadingSpinner } from '../../components/common';
import { setFilters, setCurrentPage, setRequestFilters, setRequestCurrentPage } from '../../store/slices/assetSlice';
import toast from 'react-hot-toast';

// AuthenticatedImage component for displaying images securely
const AuthenticatedImage = ({ fileId, authToken, className, onClick, ...props }) => {
  const [imageUrl, setImageUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!fileId || !authToken) return;

    const loadImage = async () => {
      try {
        setLoading(true);
        setError(false);

        const response = await fetch(`${process.env.REACT_APP_API_URL}/assets/images/${fileId}?token=${authToken}`);
        if (!response.ok) {
          throw new Error('Failed to load image');
        }

        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        setImageUrl(url);
      } catch (err) {
        console.error('Error loading authenticated image:', err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    loadImage();

    // Cleanup blob URL
    return () => {
      if (imageUrl) {
        URL.revokeObjectURL(imageUrl);
      }
    };
  }, [fileId, authToken]);

  if (loading) {
    return (
      <div className={`${className} bg-gray-100 flex items-center justify-center`}>
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`${className} bg-gray-100 flex items-center justify-center text-gray-400`}>
        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd"/>
        </svg>
      </div>
    );
  }

  return (
    <img
      src={imageUrl}
      className={className}
      onClick={onClick}
      {...props}
    />
  );
};

const AdminAssetManagement = () => {
  const { t } = useTranslation(['common', 'assets']);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { filters, pagination, requestFilters, requestPagination } = useSelector((state) => state.asset);

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showScheduleMaintenanceModal, setShowScheduleMaintenanceModal] = useState(false);
  const [showStartMaintenanceModal, setShowStartMaintenanceModal] = useState(false);
  const [showCompleteMaintenanceModal, setShowCompleteMaintenanceModal] = useState(false);
  const [showCancelMaintenanceModal, setShowCancelMaintenanceModal] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [activeTab, setActiveTab] = useState('assets'); // 'assets' or 'requests'

  // Form states
  const [assetForm, setAssetForm] = useState({
    assetName: '',
    category: '',
    model: '',
    serialNumber: '',
    vendor: '',
    purchaseDate: '',
    purchaseCost: '',
    warrantyExpiry: '',
    branchId: ''
  });

  // Image upload states
  const [selectedImages, setSelectedImages] = useState([]);
  const [imagePreviewModal, setImagePreviewModal] = useState({ isOpen: false, image: null });
  const galleryInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const [assignForm, setAssignForm] = useState({
    employeeId: '',
    condition: 'good',
    notes: ''
  });

  const [remarks, setRemarks] = useState('');

  const [transferForm, setTransferForm] = useState({
    toEmployeeId: '',
    condition: 'good',
    reason: 'reassignment',
    notes: ''
  });

  const [maintenanceForm, setMaintenanceForm] = useState({
    type: 'preventive',
    description: '',
    startDate: '',
    cost: '',
    vendor: '',
    technician: '',
    completionType: 'complete',
    workPerformed: '',
    actualCost: '',
    additionalNotes: ''
  });

  // Employee search states for assign modal
  const [employeeSearchTerm, setEmployeeSearchTerm] = useState('');
  const [debouncedEmployeeSearch, setDebouncedEmployeeSearch] = useState('');

// API hooks
  const { data: assetsData, isLoading: assetsLoading, refetch: refetchAssets } = useGetAssetsQuery({
    page: pagination.currentPage,
    limit: pagination.limit,
    ...filters
  });

  // Get auth token for image requests
  const authToken = useSelector(state => state.auth.token);

  const { data: requestsData, isLoading: requestsLoading, refetch: refetchRequests } = useGetAssetRequestsQuery({
    page: requestPagination.currentPage,
    limit: requestPagination.limit,
    ...requestFilters
  });

  const [createAsset, { isLoading: creating }] = useCreateAssetMutation();
  const [updateAsset, { isLoading: updating }] = useUpdateAssetMutation();
  const [assignAsset, { isLoading: assigning }] = useAssignAssetMutation();
  const [returnAsset, { isLoading: returning }] = useReturnAssetMutation();
  const [transferAsset, { isLoading: transferring }] = useTransferAssetMutation();
  const [approveRequest, { isLoading: approving }] = useApproveAssetRequestMutation();
  const [rejectRequest, { isLoading: rejecting }] = useRejectAssetRequestMutation();
  const [fulfillRequest, { isLoading: fulfilling }] = useFulfillAssetRequestMutation();
  const [scheduleMaintenance, { isLoading: schedulingMaintenance }] = useScheduleMaintenanceMutation();
  const [startMaintenance, { isLoading: startingMaintenance }] = useStartMaintenanceMutation();
  const [completeMaintenance, { isLoading: completingMaintenance }] = useCompleteMaintenanceMutation();
  const [cancelMaintenance, { isLoading: cancelingMaintenance }] = useCancelMaintenanceMutation();

  // Get branches for dropdown
  const { data: branchesData, isLoading: branchesLoading } = useGetBranchesQuery();

  // Get asset history for selected asset
  const { data: assetHistory, isLoading: historyLoading } = useGetAssetHistoryQuery(
    selectedAsset?._id,
    {
      skip: !selectedAsset?._id || !showHistoryModal
    }
  );

  // Debounce employee search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedEmployeeSearch(employeeSearchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [employeeSearchTerm]);

  // Get employees for assign modal with filtering - only when there's a search term
  const { data: employeesData, isLoading: employeesLoading } = useGetEmployeesQuery(
    {
      branchId: selectedAsset?.branchId?._id || selectedAsset?.branchId,
      search: debouncedEmployeeSearch,
      status: 'active',
      limit: 20
    },
    {
      skip: !selectedAsset?.branchId || debouncedEmployeeSearch.trim().length === 0
    }
  );

  // Reset search when modal opens/closes
  useEffect(() => {
    if (showAssignModal) {
      setEmployeeSearchTerm('');
      setDebouncedEmployeeSearch('');
    }
  }, [showAssignModal]);

  // Category options
  const categoryOptions = [
    { value: 'laptop', label: 'Laptop', icon: ComputerDesktopIcon },
    { value: 'desktop', label: 'Desktop', icon: ComputerDesktopIcon },
    { value: 'printer', label: 'Printer', icon: PrinterIcon },
    { value: 'scanner', label: 'Scanner', icon: CpuChipIcon },
    { value: 'mobile', label: 'Mobile', icon: DevicePhoneMobileIcon },
    { value: 'tablet', label: 'Tablet', icon: DevicePhoneMobileIcon },
    { value: 'monitor', label: 'Monitor', icon: ComputerDesktopIcon },
    { value: 'keyboard', label: 'Keyboard', icon: CpuChipIcon },
    { value: 'mouse', label: 'Mouse', icon: CpuChipIcon },
    { value: 'headset', label: 'Headset', icon: CpuChipIcon },
    { value: 'projector', label: 'Projector', icon: CpuChipIcon },
    { value: 'furniture', label: 'Furniture', icon: WrenchScrewdriverIcon },
    { value: 'equipment', label: 'Equipment', icon: WrenchScrewdriverIcon },
    { value: 'software_license', label: 'Software License', icon: CpuChipIcon },
    { value: 'other', label: 'Other', icon: WrenchScrewdriverIcon }
  ];

  // Status options
  const statusOptions = [
    { value: '', label: 'All Statuses' },
    { value: 'available', label: 'Available', color: 'bg-green-100 text-green-800' },
    { value: 'assigned', label: 'Assigned', color: 'bg-blue-100 text-blue-800' },
    { value: 'under_maintenance', label: 'Under Maintenance', color: 'bg-yellow-100 text-yellow-800' },
    { value: 'retired', label: 'Retired', color: 'bg-red-100 text-red-800' }
  ];

  // Request status options
  const requestStatusOptions = [
    { value: '', label: 'All Statuses' },
    { value: 'submitted', label: 'Submitted', color: 'bg-yellow-100 text-yellow-800' },
    { value: 'approved', label: 'Approved', color: 'bg-green-100 text-green-800' },
    { value: 'fulfilled', label: 'Fulfilled', color: 'bg-purple-100 text-purple-800' },
    { value: 'rejected', label: 'Rejected', color: 'bg-red-100 text-red-800' }
  ];

  // Handlers
  const handleFilterChange = (name, value) => {
    dispatch(setFilters({ [name]: value }));
  };

  const handleRequestFilterChange = (name, value) => {
    dispatch(setRequestFilters({ [name]: value }));
  };

  const getCategoryIcon = (category) => {
    const option = categoryOptions.find(opt => opt.value === category);
    return option ? option.icon : WrenchScrewdriverIcon;
  };

  const getCategoryColor = (category) => {
    const colors = {
      laptop: 'bg-blue-100 text-blue-800',
      desktop: 'bg-indigo-100 text-indigo-800',
      printer: 'bg-red-100 text-red-800',
      scanner: 'bg-purple-100 text-purple-800',
      mobile: 'bg-green-100 text-green-800',
      tablet: 'bg-cyan-100 text-cyan-800',
      default: 'bg-gray-100 text-gray-800'
    };
    return colors[category] || colors.default;
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'available':
        return <CheckCircleSolidIcon className="h-4 w-4 text-green-600" />;
      case 'assigned':
        return <UserSolidIcon className="h-4 w-4 text-blue-600" />;
      case 'under_maintenance':
        return <ClockSolidIcon className="h-4 w-4 text-yellow-600" />;
      case 'retired':
        return <XCircleSolidIcon className="h-4 w-4 text-red-600" />;
      default:
        return <ClockSolidIcon className="h-4 w-4 text-gray-600" />;
    }
  };

  const getEventIcon = (eventType) => {
    switch (eventType) {
      case 'assigned':
        return <UserSolidIcon className="h-4 w-4 text-white" />;
      case 'returned':
        return <TruckIcon className="h-4 w-4 text-white" />;
      case 'transferred':
        return <ArrowsRightLeftIcon className="h-4 w-4 text-white" />;
      case 'created':
        return <PlusIcon className="h-4 w-4 text-white" />;
      default:
        return <ClockSolidIcon className="h-4 w-4 text-white" />;
    }
  };

  const getEventColor = (eventType) => {
    switch (eventType) {
      case 'assigned':
        return 'bg-green-50 border-green-200';
      case 'returned':
        return 'bg-blue-50 border-blue-200';
      case 'transferred':
        return 'bg-orange-50 border-orange-200';
      case 'created':
        return 'bg-purple-50 border-purple-200';
      default:
        return 'bg-gray-50 border-gray-200';
    }
  };

  const getEventIconBg = (eventType) => {
    switch (eventType) {
      case 'assigned':
        return 'bg-green-500';
      case 'returned':
        return 'bg-blue-500';
      case 'transferred':
        return 'bg-orange-500';
      case 'created':
        return 'bg-purple-500';
      default:
        return 'bg-gray-500';
    }
  };

  // Image upload handlers
  const handleImageSelect = (files) => {
    const validImages = Array.from(files).filter(file => {
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      const maxSize = 2 * 1024 * 1024; // 2   MB

      if (!validTypes.includes(file.type)) {
        toast.error(`Invalid file type: ${file.name}. Only JPG, PNG, GIF, and WebP are allowed.`);
        return false;
      }

      if (file.size > maxSize) {
        toast.error(`File ${file.name} is too large. Maximum size is 2MB.`);
        return false;
      }

      return true;
    });

    if (validImages.length + selectedImages.length > 4) {
      toast.error('Maximum 4 images allowed per asset');
      return;
    }

    setSelectedImages(prev => [...prev, ...validImages]);
  };

  const handleGallerySelect = () => {
    galleryInputRef.current?.click();
  };

  const handleCameraSelect = () => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      toast.error('Camera not supported on this device');
      return;
    }
    cameraInputRef.current?.click();
  };

  const removeImage = (index) => {
    setSelectedImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleImagePreview = async (imageSrc, fileId, isBlob = false) => {
    try {
      if (isBlob) {
        // Already a blob URL
        setImagePreviewModal({ isOpen: true, image: imageSrc });
      } else if (fileId && authToken) {
        // Fetch from server and create blob URL
        const response = await fetch(`${process.env.REACT_APP_API_URL}/assets/images/${fileId}?token=${authToken}`);
        if (response.ok) {
          const blob = await response.blob();
          const blobUrl = URL.createObjectURL(blob);
          setImagePreviewModal({ isOpen: true, image: blobUrl });
        } else {
          console.error('Failed to fetch image:', response.status, response.statusText);
          toast.error('Failed to load image preview');
        }
      } else {
        // Direct URL for new images
        setImagePreviewModal({ isOpen: true, image: imageSrc });
      }
    } catch (error) {
      console.error('Error loading image preview:', error);
      toast.error('Failed to load image preview');
    }
  };

  // Function to get authenticated image URL as blob
  const getAuthenticatedImageUrl = async (fileId) => {
    try {
      if (!fileId || !authToken) return null;

      const response = await fetch(`${process.env.REACT_APP_API_URL}/assets/images/${fileId}?token=${authToken}`);
      if (!response.ok) {
        console.error('Failed to fetch authenticated image:', response.status, response.statusText);
        return null;
      }

      const blob = await response.blob();
      return URL.createObjectURL(blob);
    } catch (error) {
      console.error('Error fetching authenticated image:', error);
      return null;
    }
  };

  const handleCreateAsset = async (e) => {
    e.preventDefault();
    try {
      // Prepare FormData with images if any
      const formData = new FormData();

      // Add form data
      Object.keys(assetForm).forEach(key => {
        if (assetForm[key] !== undefined && assetForm[key] !== null) {
          formData.append(key, assetForm[key]);
        }
      });

      // Add images
      if (selectedImages.length > 0) {
        selectedImages.forEach((image, index) => {
          formData.append('images', image);
        });
      }

      await createAsset(formData).unwrap();
      toast.success('Asset created successfully');
      setShowCreateModal(false);
      setAssetForm({
        assetName: '',
        category: '',
        model: '',
        serialNumber: '',
        vendor: '',
        purchaseDate: '',
        purchaseCost: '',
        warrantyExpiry: '',
        branchId: ''
      });
      setSelectedImages([]);
      refetchAssets();
    } catch (error) {
      toast.error(error.data?.message || 'Failed to create asset');
    }
  };

  const handleAssignAsset = async (asset) => {
    setSelectedAsset(asset);
    setAssignForm({
      employeeId: '',
      condition: 'good',
      notes: ''
    });
    setShowAssignModal(true);
  };

  const handleAssignSubmit = async (e) => {
    e.preventDefault();
    try {
      await assignAsset({
        id: selectedAsset._id,
        employeeId: assignForm.employeeId,
        condition: assignForm.condition,
        notes: assignForm.notes
      }).unwrap();
      toast.success('Asset assigned successfully');
      setShowAssignModal(false);
      setSelectedAsset(null);
      refetchAssets();
    } catch (error) {
      toast.error(error.data?.message || 'Failed to assign asset');
    }
  };

  const handleReturnAsset = async (asset) => {
    setSelectedAsset(asset);
    setAssignForm({
      ...assignForm,
      condition: 'good'
    });
    setShowReturnModal(true);
  };

  const handleReturnSubmit = async (e) => {
    e.preventDefault();
    try {
      await returnAsset({
        id: selectedAsset._id,
        condition: assignForm.condition
      }).unwrap();
      toast.success('Asset returned successfully');
      setShowReturnModal(false);
      setSelectedAsset(null);
      refetchAssets();
    } catch (error) {
      toast.error(error.data?.message || 'Failed to return asset');
    }
  };

  const handleApproveRequest = async (request) => {
    setSelectedRequest(request);
    setRemarks(request.finalDecision?.remarks || '');
    try {
      await approveRequest({ id: request._id, remarks }).unwrap();
      toast.success('Request approved successfully');
      refetchRequests();
    } catch (error) {
      toast.error(error.data?.message || 'Failed to approve request');
    }
  };

  const handleTransferAsset = async (asset) => {
    setSelectedAsset(asset);
    setTransferForm({
      toEmployeeId: '',
      condition: 'good',
      reason: 'reassignment',
      notes: ''
    });
    setEmployeeSearchTerm('');
    setShowTransferModal(true);
  };

  const handleTransferSubmit = async (e) => {
    e.preventDefault();
    try {
      await transferAsset({
        id: selectedAsset._id,
        toEmployeeId: transferForm.toEmployeeId,
        condition: transferForm.condition,
        reason: transferForm.reason,
        notes: transferForm.notes
      }).unwrap();
      toast.success('Asset transferred successfully');
      setShowTransferModal(false);
      setSelectedAsset(null);
      refetchAssets();
    } catch (error) {
      toast.error(error.data?.message || 'Failed to transfer asset');
    }
  };

  const handleRejectRequest = async (request) => {
    setSelectedRequest(request);
    setRemarks('');
    // Here we would open a rejection modal - for now just reject directly
    try {
      const remarks = 'Request rejected by admin';
      await rejectRequest({ id: request._id, remarks }).unwrap();
      toast.success('Request rejected successfully');
      refetchRequests();
    } catch (error) {
      toast.error(error.data?.message || 'Failed to reject request');
    }
  };

  // Maintenance handlers
  const handleScheduleMaintenance = (asset) => {
    setSelectedAsset(asset);
    setMaintenanceForm({
      type: 'preventive',
      description: '',
      startDate: '',
      cost: '',
      vendor: '',
      technician: ''
    });
    setShowScheduleMaintenanceModal(true);
  };

  const handleStartMaintenance = (asset) => {
    setSelectedAsset(asset);
    setShowStartMaintenanceModal(true);
  };

  const handleCompleteMaintenance = (asset) => {
    setSelectedAsset(asset);

    // Find the in-progress maintenance to pre-fill form
    const inProgressMaintenance = asset.maintenanceHistory?.find(m => m.status === 'in_progress');

    setMaintenanceForm({
      type: inProgressMaintenance?.type || 'preventive',
      description: inProgressMaintenance?.description || '',
      startDate: inProgressMaintenance?.startDate ? new Date(inProgressMaintenance.startDate).toISOString().slice(0, 16) : '',
      cost: inProgressMaintenance?.cost || '',
      vendor: inProgressMaintenance?.vendor || '',
      technician: inProgressMaintenance?.technician || '',
      completionType: 'complete',
      workPerformed: '',
      actualCost: inProgressMaintenance?.cost || '',
      additionalNotes: ''
    });
    setShowCompleteMaintenanceModal(true);
  };

  const handleCancelMaintenance = (asset) => {
    setSelectedAsset(asset);
    setRemarks('');
    setShowCancelMaintenanceModal(true);
  };

  const handleScheduleMaintenanceSubmit = async (e) => {
    e.preventDefault();
    try {
      await scheduleMaintenance({
        id: selectedAsset._id,
        type: maintenanceForm.type,
        description: maintenanceForm.description,
        startDate: maintenanceForm.startDate,
        cost: maintenanceForm.cost,
        vendor: maintenanceForm.vendor,
        technician: maintenanceForm.technician
      }).unwrap();
      toast.success('Maintenance scheduled successfully');
      setShowScheduleMaintenanceModal(false);
      setSelectedAsset(null);
      refetchAssets();
    } catch (error) {
      toast.error(error.data?.message || 'Failed to schedule maintenance');
    }
  };

  const handleStartMaintenanceSubmit = async () => {
    try {
      // Find the maintenance index that is scheduled
      const scheduledMaintenance = selectedAsset.maintenanceHistory?.find(m => m.status === 'scheduled');
      if (!scheduledMaintenance) {
        toast.error('No scheduled maintenance found');
        return;
      }
      const maintenanceIndex = selectedAsset.maintenanceHistory.indexOf(scheduledMaintenance);

      await startMaintenance({
        id: selectedAsset._id,
        maintenanceIndex
      }).unwrap();
      toast.success('Maintenance started successfully');
      setShowStartMaintenanceModal(false);
      setSelectedAsset(null);
      refetchAssets();
    } catch (error) {
      toast.error(error.data?.message || 'Failed to start maintenance');
    }
  };

  const handleCompleteMaintenanceSubmit = async (e) => {
    e.preventDefault();
    try {
      // Find the maintenance index that is in_progress
      const inProgressMaintenance = selectedAsset.maintenanceHistory?.find(m => m.status === 'in_progress');
      if (!inProgressMaintenance) {
        toast.error('No ongoing maintenance found');
        return;
      }
      const maintenanceIndex = selectedAsset.maintenanceHistory.indexOf(inProgressMaintenance);

      // Combine work performed and additional notes
      const combinedNotes = [maintenanceForm.workPerformed, maintenanceForm.additionalNotes]
        .filter(Boolean)
        .join('\n\n');

      await completeMaintenance({
        id: selectedAsset._id,
        maintenanceIndex,
        cost: maintenanceForm.actualCost || maintenanceForm.cost,
        technician: maintenanceForm.technician,
        vendor: maintenanceForm.vendor,
        notes: combinedNotes
      }).unwrap();
      toast.success('Maintenance completed successfully');
      setShowCompleteMaintenanceModal(false);
      setSelectedAsset(null);
      refetchAssets();
    } catch (error) {
      toast.error(error.data?.message || 'Failed to complete maintenance');
    }
  };

  const handleCancelMaintenanceSubmit = async (e) => {
    e.preventDefault();
    try {
      // Find the maintenance index that is in_progress
      const inProgressMaintenance = selectedAsset.maintenanceHistory?.find(m => m.status === 'in_progress');
      if (!inProgressMaintenance) {
        toast.error('No ongoing maintenance found');
        return;
      }
      const maintenanceIndex = selectedAsset.maintenanceHistory.indexOf(inProgressMaintenance);

      await cancelMaintenance({
        id: selectedAsset._id,
        maintenanceIndex
      }).unwrap();
      toast.success('Maintenance canceled successfully');
      setShowCancelMaintenanceModal(false);
      setSelectedAsset(null);
      setRemarks('');
      refetchAssets();
    } catch (error) {
      toast.error(error.data?.message || 'Failed to cancel maintenance');
    }
  };

  // Asset columns
  const assetColumns = [
    {
      key: 'assetId',
      header: 'Asset ID',
      render: (row) => (
        <div className="font-medium text-gray-900">
          {row.assetId}
        </div>
      )
    },
    {
      key: 'assetName',
      header: 'Asset Name',
      render: (row) => (
        <div className="flex items-center space-x-2">
          {React.createElement(getCategoryIcon(row.category), {
            className: "h-4 w-4 text-gray-600"
          })}
          <div>
            <div className="font-medium text-gray-900">{row.assetName}</div>
            <div className="text-sm text-gray-500">{row.model}</div>
          </div>
        </div>
      )
    },
    {
      key: 'category',
      header: 'Category',
      render: (row) => (
        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(row.category)}`}>
          {categoryOptions.find(opt => opt.value === row.category)?.label}
        </span>
      )
    },
    {
      key: 'currentStatus',
      header: 'Status',
      render: (row) => (
        <div className="flex items-center">
          {getStatusIcon(row.currentStatus)}
          <span className="ml-1 capitalize">{row.currentStatus?.replace('_', ' ')}</span>
        </div>
      )
    },
    {
      key: 'currentHolder',
      header: 'Assigned To',
      render: (row) => (
        <div>
          {row.currentHolder ? (
            <div className="text-sm">
              <div className="font-medium text-gray-900">
                {row.currentHolder.firstName} {row.currentHolder.lastName}
              </div>
              <div className="text-gray-500">{row.currentHolder.employeeId}</div>
            </div>
          ) : (
            <span className="text-gray-500">Unassigned</span>
          )}
        </div>
      )
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (row) => (
        <div className="flex items-center space-x-1">
          <button
            onClick={() => handleViewDetails(row)}
            className="p-1 text-gray-400 hover:text-gray-600"
            title="View Details"
          >
            <EyeIcon className="h-4 w-4" />
          </button>
          <button
            onClick={() => handleEditAsset(row)}
            className="p-1 text-indigo-400 hover:text-indigo-600"
            title="Edit Asset"
          >
            <PencilIcon className="h-4 w-4" />
          </button>
          {row.currentStatus === 'available' && (
            <button
              onClick={() => handleAssignAsset(row)}
              className="p-1 text-green-400 hover:text-green-600"
              title="Assign Asset"
            >
              <UserSolidIcon className="h-4 w-4" />
            </button>
          )}
          {row.currentStatus === 'assigned' && (
            <>
            <button
              onClick={() => handleTransferAsset(row)}
              className="p-1 text-orange-400 hover:text-orange-600"
              title="Transfer Asset"
            >
              <ArrowsRightLeftIcon className="h-4 w-4" />
            </button>
            <button
              onClick={() => handleReturnAsset(row)}
              className="p-1 text-blue-400 hover:text-blue-600"
              title="Return Asset"
            >
              <TruckIcon className="h-4 w-4" />
            </button>
            </>
          )}
          {/* Schedule Maintenance - Available for available/assigned assets */}
          {(row.currentStatus !== 'under_maintenance' && row.currentStatus !== 'retired' && row.currentStatus == 'available') && (
            <button
              onClick={() => handleScheduleMaintenance(row)}
              className="p-1 text-amber-400 hover:text-amber-600 hover:bg-amber-50 rounded transition-colors duration-200"
              title="Schedule Maintenance"
            >
              <CalendarDaysIcon className="h-4 w-4" />
            </button>
          )}

          {/* Start Scheduled Maintenance */}
          {row.currentStatus === 'under_maintenance' && row.maintenanceHistory && row.maintenanceHistory.some(m => m.status === 'scheduled') && !row.maintenanceHistory.some(m => m.status === 'in_progress') && (
            <button
              onClick={() => handleStartMaintenance(row)}
              className="p-1 text-blue-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors duration-200"
              title="Start Maintenance"
            >
              <PlayIcon className="h-4 w-4" />
            </button>
          )}

          {/* Complete Ongoing Maintenance */}
          {row.currentStatus === 'under_maintenance' && row.maintenanceHistory && row.maintenanceHistory.some(m => m.status === 'in_progress') && (
            <button
              onClick={() => handleCompleteMaintenance(row)}
              className="p-1 text-green-400 hover:text-green-600 hover:bg-green-50 rounded transition-colors duration-200"
              title="Complete Maintenance"
            >
              <CheckCircleIcon className="h-4 w-4" />
            </button>
          )}

          {/* Cancel Ongoing Maintenance */}
          {row.currentStatus === 'under_maintenance' && row.maintenanceHistory && row.maintenanceHistory.some(m => m.status === 'in_progress') && (
            <button
              onClick={() => handleCancelMaintenance(row)}
              className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors duration-200"
              title="Cancel Maintenance"
            >
              <XCircleIcon className="h-4 w-4" />
            </button>
          )}
          <button
            onClick={() => handleViewHistory(row)}
            className="p-1 text-purple-400 hover:text-purple-600"
            title="View Assignment History"
          >
            <ClockIcon className="h-4 w-4" />
          </button>
        </div>
      )
    }
  ];

  // Request columns
  const requestColumns = [
    {
      key: 'requestNumber',
      header: 'Request #',
      render: (row) => (
        <div className="font-medium text-gray-900">
          {row.requestNumber}
        </div>
      )
    },
    {
      key: 'employeeId',
      header: 'Employee',
      render: (row) => (
        <div className="text-sm">
          <div className="font-medium text-gray-900">
            {row.employeeId?.firstName} {row.employeeId?.lastName}
          </div>
          <div className="text-gray-500">{row.employeeId?.employeeId}</div>
        </div>
      )
    },
    {
      key: 'assetType',
      header: 'Requested Asset',
      render: (row) => (
        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(row.assetType)}`}>
          {categoryOptions.find(opt => opt.value === row.assetType)?.label}
        </span>
      )
    },
    {
      key: 'priority',
      header: 'Priority',
      render: (row) => (
        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
          row.priority === 'urgent' ? 'bg-red-100 text-red-800' :
          row.priority === 'high' ? 'bg-orange-100 text-orange-800' :
          row.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
          'bg-green-100 text-green-800'
        }`}>
          {row.priority}
        </span>
      )
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => (
        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
          row.status === 'approved' ? 'bg-green-100 text-green-800' :
          row.status === 'rejected' ? 'bg-red-100 text-red-800' :
          row.status === 'fulfilled' ? 'bg-purple-100 text-purple-800' :
          'bg-yellow-100 text-yellow-800'
        }`}>
          {row.status}
        </span>
      )
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (row) => (
        <div className="flex items-center space-x-1">
          <button
            onClick={() => {
              setSelectedRequest(row);
              setShowRequestModal(true);
            }}
            className="p-1 text-gray-400 hover:text-gray-600"
            title="View Details"
          >
            <EyeIcon className="h-4 w-4" />
          </button>
          {(row.status === 'submitted') && (
            <>
          <button
            onClick={() => handleViewDetails(row)}
            className="p-1 text-gray-400 hover:text-gray-600"
            title="View Details"
          >
            <EyeIcon className="h-4 w-4" />
          </button>
          <button
            onClick={() => handleViewHistory(row)}
            className="p-1 text-purple-400 hover:text-purple-600"
            title="View Assignment History"
          >
            <ClockIcon className="h-4 w-4" />
          </button>
            </>
          )}
        </div>
      )
    }
  ];

  const handleViewDetails = (asset) => {
    setSelectedAsset(asset);
    setShowDetailsModal(true);
  };

  const handleViewHistory = (asset) => {
    setSelectedAsset(asset);
    setShowHistoryModal(true);
  };

  const handleEditAsset = (asset) => {
    setSelectedAsset(asset);
    // Pre-populate form with existing asset data
    setAssetForm({
      assetName: asset.assetName || '',
      category: asset.category || '',
      model: asset.model || '',
      serialNumber: asset.serialNumber || '',
      vendor: asset.vendor || '',
      purchaseDate: asset.purchaseDate ? new Date(asset.purchaseDate).toISOString().split('T')[0] : '',
      purchaseCost: asset.purchaseCost || '',
      warrantyExpiry: asset.warrantyExpiry ? new Date(asset.warrantyExpiry).toISOString().split('T')[0] : '',
      branchId: asset.branchId?._id || asset.branchId || ''
    });
    // Existing images will be handled by the API - clear new images for edit
    setSelectedImages([]);
    setShowEditModal(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      const formData = new FormData();

      // Add form data - only include fields that have changed or are not empty
      Object.keys(assetForm).forEach(key => {
        if (assetForm[key] !== undefined && assetForm[key] !== null && assetForm[key] !== '') {
          formData.append(key, assetForm[key]);
        }
      });

      // Add new images
      if (selectedImages.length > 0) {
        selectedImages.forEach((image, index) => {
          formData.append('newImages', image);
        });
      }

      // Add images to remove (if we had that functionality)
      // We could add state to track images to remove

      await updateAsset({
        id: selectedAsset._id,
        formData
      }).unwrap();
      toast.success('Asset updated successfully');
      setShowEditModal(false);
      setSelectedAsset(null);
      setSelectedImages([]);
      refetchAssets();
    } catch (error) {
      toast.error(error.data?.message || 'Failed to update asset');
    }
  };

  return (
    <>
      <Helmet>
        <title>Asset Management - HRMS</title>
      </Helmet>

      {/* Header */}
      <div className="bg-white/80 backdrop-blur-sm border-b border-white/20 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 sm:h-20">
            <div className="flex items-center space-x-2 sm:space-x-4 min-w-0 flex-1">
              <button
                onClick={() => navigate('/dashboard')}
                className="flex items-center px-2 sm:px-4 py-2 text-gray-600 hover:text-gray-900 bg-white/50 rounded-lg sm:rounded-xl hover:bg-white/80 transition-all duration-200 group flex-shrink-0"
              >
                <svg className="h-4 w-4 sm:h-5 sm:w-5 mr-1 sm:mr-2 group-hover:-translate-x-1 transition-transform duration-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.5 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                <span className="hidden xs:inline">Back</span>
              </button>
              <div className="min-w-0 flex-1">
                <h1 className="text-xl sm:text-2xl md:text-3xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent truncate flex items-center">
                  <ComputerDesktopIcon className="h-6 w-6 sm:h-8 sm:w-8 mr-2 sm:mr-3 text-blue-600" />
                  Asset Management
                </h1>
                <p className="text-xs sm:text-sm text-gray-600 mt-1 hidden sm:block">
                  Manage company assets and employee requests
                </p>
              </div>
            </div>
            <Button
              onClick={() => setShowCreateModal(true)}
              className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              Add Asset
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-3 sm:py-4">

          {/* Tabs */}
          <div className="bg-white/90 backdrop-blur-sm rounded-lg border border-white/60 p-1 mb-4">
            <div className="flex space-x-1">
              <button
                onClick={() => setActiveTab('assets')}
                className={`flex-1 px-4 py-2 rounded-md font-medium transition-all duration-200 ${
                  activeTab === 'assets'
                    ? 'bg-blue-500 text-white shadow-md'
                    : 'text-gray-600 hover:text-blue-600'
                }`}
              >
                Assets ({assetsData?.stats?.totalAssets || 0})
              </button>
              <button
                onClick={() => setActiveTab('requests')}
                className={`flex-1 px-4 py-2 rounded-md font-medium transition-all duration-200 ${
                  activeTab === 'requests'
                    ? 'bg-blue-500 text-white shadow-md'
                    : 'text-gray-600 hover:text-blue-600'
                }`}
              >
                Requests ({requestsData?.stats?.totalRequests || 0})
              </button>
            </div>
          </div>

          {/* Assets Tab */}
          {activeTab === 'assets' && (
            <>
              {/* Stats */}
              {assetsData?.stats && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                  <div className="bg-white/90 backdrop-blur-sm rounded-lg border border-white/60 p-3 shadow-sm">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <div className="p-1.5 bg-green-100 rounded-md">
                          <CheckCircleIcon className="h-4 w-4 text-green-600" />
                        </div>
                        <div>
                          <p className="text-xs font-medium text-gray-600">Available</p>
                          <p className="text-lg font-bold text-gray-900">{assetsData.stats.availableAssets}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white/90 backdrop-blur-sm rounded-lg border border-white/60 p-3 shadow-sm">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <div className="p-1.5 bg-blue-100 rounded-md">
                          <UserSolidIcon className="h-4 w-4 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-xs font-medium text-gray-600">Assigned</p>
                          <p className="text-lg font-bold text-gray-900">{assetsData.stats.assignedAssets}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white/90 backdrop-blur-sm rounded-lg border border-white/60 p-3 shadow-sm">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <div className="p-1.5 bg-yellow-100 rounded-md">
                          <WrenchScrewdriverIcon className="h-4 w-4 text-yellow-600" />
                        </div>
                        <div>
                          <p className="text-xs font-medium text-gray-600">Maintenance</p>
                          <p className="text-lg font-bold text-gray-900">{assetsData.stats.underMaintenanceAssets}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white/90 backdrop-blur-sm rounded-lg border border-white/60 p-3 shadow-sm">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <div className="p-1.5 bg-purple-100 rounded-md">
                          <BarsArrowUpIcon className="h-4 w-4 text-purple-600" />
                        </div>
                        <div>
                          <p className="text-xs font-medium text-gray-600">Total Value</p>
                          <p className="text-lg font-bold text-gray-900">₹{(assetsData.stats.totalValue || 0).toLocaleString()}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Filters and Assets Table */}
              <div className="bg-white/90 backdrop-blur-sm rounded-lg border border-white/60 p-3 shadow-sm">
                <div className="flex flex-col sm:flex-row gap-4 mb-4">
                  <Input
                    placeholder="Search assets..."
                    value={filters.search}
                    onChange={(e) => handleFilterChange('search', e.target.value)}
                    className="sm:w-80"
                  />
                  <Select
                    value={filters.status}
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                    className="sm:w-40"
                  >
                    {statusOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                  <Select
                    value={filters.category}
                    onChange={(e) => handleFilterChange('category', e.target.value)}
                    className="sm:w-40"
                  >
                    <option value="">All Categories</option>
                    {categoryOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                </div>

                <DataTable
                  columns={assetColumns}
                  data={assetsData?.data || []}
                  loading={assetsLoading}
                  pagination={assetsData?.pagination}
                  onPageChange={(page) => dispatch(setCurrentPage(page))}
                  emptyMessage="No assets found"
                />
              </div>
            </>
          )}

          {/* Requests Tab */}
          {activeTab === 'requests' && (
            <>
              {/* Request Stats */}
              {requestsData?.stats && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                  <div className="bg-white/90 backdrop-blur-sm rounded-lg border border-white/60 p-3 shadow-sm">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <div className="p-1.5 bg-yellow-100 rounded-md">
                          <ClockSolidIcon className="h-4 w-4 text-yellow-600" />
                        </div>
                        <div>
                          <p className="text-xs font-medium text-gray-600">Pending</p>
                          <p className="text-lg font-bold text-gray-900">{requestsData.stats.submittedRequests}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white/90 backdrop-blur-sm rounded-lg border border-white/60 p-3 shadow-sm">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <div className="p-1.5 bg-green-100 rounded-md">
                          <CheckCircleSolidIcon className="h-4 w-4 text-green-600" />
                        </div>
                        <div>
                          <p className="text-xs font-medium text-gray-600">Approved</p>
                          <p className="text-lg font-bold text-gray-900">{requestsData.stats.approvedRequests}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white/90 backdrop-blur-sm rounded-lg border border-white/60 p-3 shadow-sm">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <div className="p-1.5 bg-purple-100 rounded-md">
                          <UserSolidIcon className="h-4 w-4 text-purple-600" />
                        </div>
                        <div>
                          <p className="text-xs font-medium text-gray-600">Fulfilled</p>
                          <p className="text-lg font-bold text-gray-900">{requestsData.stats.fulfilledRequests}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white/90 backdrop-blur-sm rounded-lg border border-white/60 p-3 shadow-sm">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <div className="p-1.5 bg-red-100 rounded-md">
                          <XCircleSolidIcon className="h-4 w-4 text-red-600" />
                        </div>
                        <div>
                          <p className="text-xs font-medium text-gray-600">Rejected</p>
                          <p className="text-lg font-bold text-gray-900">{requestsData.stats.totalRequests - requestsData.stats.approvedRequests - requestsData.stats.fulfilledRequests - requestsData.stats.submittedRequests}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Request Filters and Table */}
              <div className="bg-white/90 backdrop-blur-sm rounded-lg border border-white/60 p-3 shadow-sm">
                <div className="flex flex-col sm:flex-row gap-4 mb-4">
                  <Input
                    placeholder="Search requests..."
                    value={requestFilters.search}
                    onChange={(e) => handleRequestFilterChange('search', e.target.value)}
                    className="sm:w-80"
                  />
                  <Select
                    value={requestFilters.status}
                    onChange={(e) => handleRequestFilterChange('status', e.target.value)}
                    className="sm:w-40"
                  >
                    {requestStatusOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                  <Select
                    value={requestFilters.assetType}
                    onChange={(e) => handleRequestFilterChange('assetType', e.target.value)}
                    className="sm:w-40"
                  >
                    <option value="">All Types</option>
                    {categoryOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                </div>

                <DataTable
                  columns={requestColumns}
                  data={requestsData?.data || []}
                  loading={requestsLoading}
                  pagination={requestsData?.pagination}
                  onPageChange={(page) => dispatch(setRequestCurrentPage(page))}
                  emptyMessage="No asset requests found"
                />
              </div>
            </>
          )}
        </div>
      </div>

      {/* Modals would go here - Create Asset, Assign, Return, Details, etc. */}
      {/* For brevity, I'll focus on the main structure */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Add New Asset"
        size="lg"
      >
        <div className="max-w-4xl mx-auto p-2">
          {/* Modal Header */}
          <div className="flex items-center justify-between mb-6 pb-3 border-b border-gray-200">
            <div className="flex items-center space-x-2">
              <div className="p-1.5 bg-blue-100 rounded-md">
                <ComputerDesktopIcon className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-900">Add New Asset</h2>
                <p className="text-xs text-gray-600">Register a new asset in your inventory</p>
              </div>
            </div>
          </div>

          <form onSubmit={handleCreateAsset} className="space-y-6">
            {/* Basic Information */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-gray-800 border-b border-gray-200 pb-2">Basic Information</h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Asset Name */}
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <CubeIcon className="h-4 w-4 text-blue-600" />
                    <label className="text-sm font-semibold text-gray-800">
                      Asset Name <span className="text-red-500">*</span>
                    </label>
                  </div>
                  <Input
                    type="text"
                    value={assetForm.assetName}
                    onChange={(e) => setAssetForm({...assetForm, assetName: e.target.value})}
                    placeholder="Enter asset name"
                    required
                    className="text-sm py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white/70 backdrop-blur-sm transition-all duration-200 hover:border-gray-300"
                  />
                </div>

                {/* Category */}
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <CubeIcon className="h-4 w-4 text-green-600" />
                    <label className="text-sm font-semibold text-gray-800">
                      Category <span className="text-red-500">*</span>
                    </label>
                  </div>
                  <select
                    value={assetForm.category}
                    onChange={(e) => setAssetForm({...assetForm, category: e.target.value})}
                    required
                    className="w-full px-3 py-3 border-2 border-gray-200 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white/70 backdrop-blur-sm transition-all duration-200 hover:border-gray-300 text-sm"
                  >
                    <option value="">Select Category</option>
                    {categoryOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Model */}
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <ComputerDesktopIcon className="h-4 w-4 text-purple-600" />
                    <label className="text-sm font-semibold text-gray-800">Model</label>
                  </div>
                  <Input
                    type="text"
                    value={assetForm.model}
                    onChange={(e) => setAssetForm({...assetForm, model: e.target.value})}
                    placeholder="Enter model name"
                    className="text-sm py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white/70 backdrop-blur-sm transition-all duration-200 hover:border-gray-300"
                  />
                </div>

                {/* Serial Number */}
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <WrenchScrewdriverIcon className="h-4 w-4 text-indigo-600" />
                    <label className="text-sm font-semibold text-gray-800">Serial Number</label>
                  </div>
                  <Input
                    type="text"
                    value={assetForm.serialNumber}
                    onChange={(e) => setAssetForm({...assetForm, serialNumber: e.target.value})}
                    placeholder="Enter serial number"
                    className="text-sm py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white/70 backdrop-blur-sm transition-all duration-200 hover:border-gray-300"
                  />
                </div>

                {/* Vendor */}
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <BuildingOfficeIcon className="h-4 w-4 text-orange-600" />
                    <label className="text-sm font-semibold text-gray-800">Vendor</label>
                  </div>
                  <Input
                    type="text"
                    value={assetForm.vendor}
                    onChange={(e) => setAssetForm({...assetForm, vendor: e.target.value})}
                    placeholder="Enter vendor name"
                    className="text-sm py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent bg-white/70 backdrop-blur-sm transition-all duration-200 hover:border-gray-300"
                  />
                </div>

                {/* Branch */}
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <BuildingOfficeIcon className="h-4 w-4 text-teal-600" />
                    <label className="text-sm font-semibold text-gray-800">Branch</label>
                  </div>
                  <select
                    value={assetForm.branchId}
                    onChange={(e) => setAssetForm({...assetForm, branchId: e.target.value})}
                    className="w-full px-3 py-3 border-2 border-gray-200 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white/70 backdrop-blur-sm transition-all duration-200 hover:border-gray-300 text-sm"
                    disabled={branchesLoading}
                  >
                    <option value="">
                      {branchesLoading ? 'Loading branches...' : 'Select Branch'}
                    </option>
                    {branchesData?.data?.map((branch) => (
                      <option key={branch._id} value={branch._id}>
                        {branch.name || branch.branchName || `Branch ${branch._id.slice(-6)}`}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Financial Information */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-gray-800 border-b border-gray-200 pb-2">Financial Information</h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Purchase Date */}
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <CalendarDaysIcon className="h-4 w-4 text-red-600" />
                    <label className="text-sm font-semibold text-gray-800">
                      Purchase Date <span className="text-red-500">*</span>
                    </label>
                  </div>
                  <Input
                    type="date"
                    value={assetForm.purchaseDate}
                    onChange={(e) => setAssetForm({...assetForm, purchaseDate: e.target.value})}
                    required
                    className="text-sm py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent bg-white/70 backdrop-blur-sm transition-all duration-200 hover:border-gray-300"
                  />
                </div>

                {/* Purchase Cost */}
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <CurrencyDollarIcon className="h-4 w-4 text-green-600" />
                    <label className="text-sm font-semibold text-gray-800">
                      Purchase Cost <span className="text-red-500">*</span>
                    </label>
                  </div>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span className="text-gray-500 text-sm font-semibold">₹</span>
                    </div>
                    <Input
                      type="number"
                      value={assetForm.purchaseCost}
                      onChange={(e) => setAssetForm({...assetForm, purchaseCost: e.target.value})}
                      placeholder="Enter purchase cost"
                      required
                      className="pl-8 text-sm py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white/70 backdrop-blur-sm transition-all duration-200 hover:border-gray-300"
                    />
                  </div>
                </div>

                {/* Warranty Expiry */}
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <ClockIcon className="h-4 w-4 text-amber-600" />
                    <label className="text-sm font-semibold text-gray-800">Warranty Expiry</label>
                  </div>
                  <Input
                    type="date"
                    value={assetForm.warrantyExpiry}
                    onChange={(e) => setAssetForm({...assetForm, warrantyExpiry: e.target.value})}
                    className="text-sm py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent bg-white/70 backdrop-blur-sm transition-all duration-200 hover:border-gray-300"
                  />
                </div>
              </div>
            </div>

            {/* Asset Images */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-gray-800 border-b border-gray-200 pb-2">Asset Images</h3>
                <span className="text-xs text-gray-500">{selectedImages.length}/10 images</span>
              </div>

              {/* Image Upload Buttons */}
              <div className="flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={handleGallerySelect}
                  className="flex items-center space-x-2 px-4 py-3 bg-purple-50 hover:bg-purple-100 text-purple-700 border-2 border-purple-200 rounded-lg transition-all duration-200 hover:scale-105 shadow-sm hover:shadow-md"
                >
                  <PhotoIcon className="h-5 w-5" />
                  <span className="text-sm font-medium">Gallery</span>
                </button>
                <button
                  type="button"
                  onClick={handleCameraSelect}
                  className="flex items-center space-x-2 px-4 py-3 bg-blue-50 hover:bg-blue-100 text-blue-700 border-2 border-blue-200 rounded-lg transition-all duration-200 hover:scale-105 shadow-sm hover:shadow-md"
                >
                  <CameraIcon className="h-5 w-5" />
                  <span className="text-sm font-medium">Camera</span>
                </button>
              </div>

              {/* Hidden File Inputs */}
              <input
                ref={galleryInputRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={(e) => handleImageSelect(e.target.files)}
              />
              <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={(e) => handleImageSelect(e.target.files)}
              />

              {/* Image Preview Grid */}
              {selectedImages.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4 p-4 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                  {selectedImages.map((image, index) => (
                    <div key={index} className="relative group">
                      <div
                        className="aspect-square bg-white rounded-lg border-2 border-gray-200 overflow-hidden shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer"
                        onClick={() => handleImagePreview(URL.createObjectURL(image))}
                      >
                        <img
                          src={URL.createObjectURL(image)}
                          alt={`Asset image ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </div>

                      {/* Remove Button */}
                      <button
                        type="button"
                        onClick={() => removeImage(index)}
                        className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center shadow-md opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                      >
                        <XMarkIcon className="h-3 w-3" />
                      </button>

                      {/* Image Info */}
                      <div className="mt-2 text-xs text-center text-gray-600 truncate">
                        {(image.size / 1024).toFixed(1)}KB
                      </div>
                    </div>
                  ))}

                  {/* Add More Placeholder */}
                  {selectedImages.length < 10 && (
                    <button
                      type="button"
                      onClick={handleGallerySelect}
                      className="aspect-square border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center text-gray-400 hover:text-gray-600 hover:border-gray-400 transition-all duration-200 hover:bg-gray-50"
                    >
                      <PlusIcon className="h-6 w-6" />
                      <span className="text-xs mt-1">Add More</span>
                    </button>
                  )}
                </div>
              )}

              {/* Upload Instructions */}
              <div className="text-xs text-gray-500 bg-blue-50 border border-blue-200 rounded-lg p-3">
                <div className="flex items-start space-x-2">
                  <DocumentIcon className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-blue-800 mb-1">Image Guidelines:</p>
                    <ul className="space-y-1 text-blue-700">
                      <li>• Maximum 4 images per asset</li>
                      <li>• Supported formats: JPG, PNG, GIF, WebP</li>
                      <li>• Maximum file size: 2MB per image</li>
                      <li>• Click on images to preview full size</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Form Actions */}
            <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-gray-200">
              <Button
                variant="secondary"
                onClick={() => setShowCreateModal(false)}
                className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transform hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-xl"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                loading={creating}
                className="flex-1 px-4 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 transform hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-xl"
              >
                {creating ? 'Creating Asset...' : 'Create Asset'}
              </Button>
            </div>
          </form>
        </div>
      </Modal>

      {/* Assign Asset Modal */}
      <Modal
        isOpen={showAssignModal}
        onClose={() => {
          setShowAssignModal(false);
          setSelectedAsset(null);
        }}
        title="Assign Asset"
        size="md"
      >
        <div className="space-y-4">
          <div className="flex items-center space-x-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <ComputerDesktopIcon className="h-8 w-8 text-blue-600" />
            <div>
              <h3 className="font-medium text-gray-900">{selectedAsset?.assetName}</h3>
              <p className="text-sm text-gray-600">Asset ID: {selectedAsset?.assetId}</p>
            </div>
          </div>

          <form onSubmit={handleAssignSubmit} className="space-y-4">
            {/* Branch Info */}
            {selectedAsset?.branchId && (
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center space-x-2">
                  <BuildingOfficeIcon className="h-4 w-4 text-blue-600" />
                  <span className="text-sm text-blue-800">
                    This asset belongs to a specific branch. Only employees from that branch can be assigned.
                  </span>
                </div>
              </div>
            )}

            {/* Employee Search */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Employee <span className="text-red-500">*</span>
              </label>
              <div className="space-y-2">
                <Input
                  type="text"
                  placeholder="Search employees (minimum 2 characters)..."
                  value={employeeSearchTerm}
                  onChange={(e) => setEmployeeSearchTerm(e.target.value)}
                  className="w-full"
                />
                <select
                  value={assignForm.employeeId}
                  onChange={(e) => setAssignForm({...assignForm, employeeId: e.target.value})}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={employeesLoading}
                >
                  <option value="">
                    {employeesLoading
                      ? 'Loading employees...'
                      : debouncedEmployeeSearch
                        ? 'Select an employee from search results'
                        : 'Start typing to search employees'
                    }
                  </option>
                  {employeesData?.data?.employees?.map((employee) => (
                    <option key={employee._id} value={employee._id}>
                      {employee.firstName} {employee.lastName} ({employee.employeeId})
                    </option>
                  ))}
                </select>
                {debouncedEmployeeSearch && employeesData?.data?.employees?.length === 0 && !employeesLoading && (
                  <p className="text-sm text-gray-500">No employees found matching your search.</p>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Condition <span className="text-red-500">*</span>
              </label>
              <select
                value={assignForm.condition}
                onChange={(e) => setAssignForm({...assignForm, condition: e.target.value})}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="excellent">Excellent</option>
                <option value="good">Good</option>
                <option value="fair">Fair</option>
                <option value="poor">Poor</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notes (Optional)
              </label>
              <textarea
                value={assignForm.notes}
                onChange={(e) => setAssignForm({...assignForm, notes: e.target.value})}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Add any notes about the assignment..."
              />
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowAssignModal(false);
                  setSelectedAsset(null);
                }}
              >
                Cancel
              </Button>
              <Button type="submit" loading={assigning}>
                {assigning ? 'Assigning...' : 'Assign Asset'}
              </Button>
            </div>
          </form>
        </div>
      </Modal>

      {/* Return Asset Modal */}
      <Modal
        isOpen={showReturnModal}
        onClose={() => {
          setShowReturnModal(false);
          setSelectedAsset(null);
        }}
        title="Return Asset"
        size="md"
      >
        <div className="space-y-4">
          <div className="flex items-center space-x-3 p-4 bg-green-50 border border-green-200 rounded-lg">
            <ComputerDesktopIcon className="h-8 w-8 text-green-600" />
            <div>
              <h3 className="font-medium text-gray-900">{selectedAsset?.assetName}</h3>
              <p className="text-sm text-gray-600">Asset ID: {selectedAsset?.assetId}</p>
              <p className="text-sm text-gray-600">
                Currently assigned to: {selectedAsset?.currentHolder?.firstName} {selectedAsset?.currentHolder?.lastName}
              </p>
            </div>
          </div>

          <form onSubmit={handleReturnSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Return Condition <span className="text-red-500">*</span>
              </label>
              <select
                value={assignForm.condition}
                onChange={(e) => setAssignForm({...assignForm, condition: e.target.value})}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                <option value="excellent">Excellent</option>
                <option value="good">Good</option>
                <option value="fair">Fair</option>
                <option value="poor">Poor</option>
                <option value="damaged">Damaged</option>
              </select>
            </div>

            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-md">
              <div className="flex items-start">
                <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 mt-0.5 mr-2" />
                <div>
                  <h4 className="text-sm font-medium text-yellow-800">Return Confirmation</h4>
                  <p className="text-sm text-yellow-700 mt-1">
                    This will mark the asset as available and end the current assignment.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowReturnModal(false);
                  setSelectedAsset(null);
                }}
              >
                Cancel
              </Button>
              <Button type="submit" loading={returning}>
                {returning ? 'Returning...' : 'Return Asset'}
              </Button>
            </div>
          </form>
        </div>
      </Modal>

      {/* Transfer Asset Modal */}
      <Modal
        isOpen={showTransferModal}
        onClose={() => {
          setShowTransferModal(false);
          setSelectedAsset(null);
        }}
        title="Transfer Asset"
        size="md"
      >
        <div className="space-y-4">
          <div className="flex items-center space-x-3 p-4 bg-orange-50 border border-orange-200 rounded-lg">
            <ArrowsRightLeftIcon className="h-8 w-8 text-orange-600" />
            <div>
              <h3 className="font-medium text-gray-900">{selectedAsset?.assetName}</h3>
              <p className="text-sm text-gray-600">Asset ID: {selectedAsset?.assetId}</p>
              <p className="text-sm text-gray-600">
                Currently assigned to: {selectedAsset?.currentHolder?.firstName} {selectedAsset?.currentHolder?.lastName}
              </p>
            </div>
          </div>

          <form onSubmit={handleTransferSubmit} className="space-y-4">
            {/* Transfer Information */}
            <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
              <div className="flex items-center space-x-2">
                <ArrowsRightLeftIcon className="h-4 w-4 text-orange-600" />
                <span className="text-sm text-orange-800">
                  This will transfer the asset from the current holder to the selected employee.
                </span>
              </div>
            </div>

            {/* Employee Search */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Transfer To Employee <span className="text-red-500">*</span>
              </label>
              <div className="space-y-2">
                <Input
                  type="text"
                  placeholder="Search employees (minimum 2 characters)..."
                  value={employeeSearchTerm}
                  onChange={(e) => setEmployeeSearchTerm(e.target.value)}
                  className="w-full"
                />
                <select
                  value={transferForm.toEmployeeId}
                  onChange={(e) => setTransferForm({...transferForm, toEmployeeId: e.target.value})}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  disabled={employeesLoading}
                >
                  <option value="">
                    {employeesLoading
                      ? 'Loading employees...'
                      : debouncedEmployeeSearch
                        ? 'Select an employee from search results'
                        : 'Start typing to search employees'
                    }
                  </option>
                  {employeesData?.data?.employees?.map((employee) => (
                    <option key={employee._id} value={employee._id}>
                      {employee.firstName} {employee.lastName} ({employee.employeeId})
                    </option>
                  ))}
                </select>
                {debouncedEmployeeSearch && employeesData?.data?.employees?.length === 0 && !employeesLoading && (
                  <p className="text-sm text-gray-500">No employees found matching your search.</p>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Reason <span className="text-red-500">*</span>
              </label>
              <select
                value={transferForm.reason}
                onChange={(e) => setTransferForm({...transferForm, reason: e.target.value})}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              >
                <option value="reassignment">Employee Reassignment</option>
                <option value="department_change">Department Change</option>
                <option value="project_transfer">Project Transfer</option>
                <option value="loan_return">Loan Return</option>
                <option value="temporary_use">Temporary Use</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Asset Condition <span className="text-red-500">*</span>
              </label>
              <select
                value={transferForm.condition}
                onChange={(e) => setTransferForm({...transferForm, condition: e.target.value})}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              >
                <option value="excellent">Excellent</option>
                <option value="good">Good</option>
                <option value="fair">Fair</option>
                <option value="poor">Poor</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Transfer Notes (Optional)
              </label>
              <textarea
                value={transferForm.notes}
                onChange={(e) => setTransferForm({...transferForm, notes: e.target.value})}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                placeholder="Add any notes about the transfer..."
              />
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowTransferModal(false);
                  setSelectedAsset(null);
                }}
              >
                Cancel
              </Button>
              <Button type="submit" loading={transferring}>
                {transferring ? 'Transferring...' : 'Transfer Asset'}
              </Button>
            </div>
          </form>
        </div>
      </Modal>

      {/* Asset Details Modal */}
      <Modal
        isOpen={showDetailsModal}
        onClose={() => {
          setShowDetailsModal(false);
          setSelectedAsset(null);
        }}
        title="Asset Details"
        size="lg"
      >
        {selectedAsset && (
          <div className="space-y-6">
            {/* Header */}
            <div className="flex items-start justify-between">
              <div className="flex items-start space-x-4">
                <div className="p-3 bg-blue-100 rounded-lg">
                  <ComputerDesktopIcon className="h-12 w-12 text-blue-600" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">{selectedAsset.assetName}</h2>
                  <p className="text-sm text-gray-600">Asset ID: {selectedAsset.assetId}</p>
                  <div className="flex items-center mt-2">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(selectedAsset.category)}`}>
                      {categoryOptions.find(opt => opt.value === selectedAsset.category)?.label}
                    </span>
                    <span className="ml-2 inline-flex items-center">
                      {getStatusIcon(selectedAsset.currentStatus)}
                      <span className="ml-1 capitalize text-sm">{selectedAsset.currentStatus?.replace('_', ' ')}</span>
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Main Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Basic Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900 border-b border-gray-200 pb-2">Basic Information</h3>
                <dl className="space-y-3">
                  <div className="flex justify-between">
                    <dt className="text-sm font-medium text-gray-500">Asset Name</dt>
                    <dd className="text-sm text-gray-900">{selectedAsset.assetName}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-sm font-medium text-gray-500">Asset ID</dt>
                    <dd className="text-sm text-gray-900">{selectedAsset.assetId}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-sm font-medium text-gray-500">Category</dt>
                    <dd className="text-sm text-gray-900">{categoryOptions.find(opt => opt.value === selectedAsset.category)?.label}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-sm font-medium text-gray-500">Model</dt>
                    <dd className="text-sm text-gray-900">{selectedAsset.model || 'N/A'}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-sm font-medium text-gray-500">Serial Number</dt>
                    <dd className="text-sm text-gray-900">{selectedAsset.serialNumber || 'N/A'}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-sm font-medium text-gray-500">Vendor</dt>
                    <dd className="text-sm text-gray-900">{selectedAsset.vendor || 'N/A'}</dd>
                  </div>
                </dl>
              </div>

              {/* Assignment Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900 border-b border-gray-200 pb-2">Assignment Information</h3>
                <dl className="space-y-3">
                  <div className="flex justify-between">
                    <dt className="text-sm font-medium text-gray-500">Status</dt>
                    <dd className="text-sm text-gray-900 capitalize">{selectedAsset.currentStatus?.replace('_', ' ')}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-sm font-medium text-gray-500">Assigned To</dt>
                    <dd className="text-sm text-gray-900">
                      {selectedAsset.currentHolder
                        ? `${selectedAsset.currentHolder.firstName} ${selectedAsset.currentHolder.lastName}`
                        : 'Unassigned'
                      }
                    </dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-sm font-medium text-gray-500">Purchase Date</dt>
                    <dd className="text-sm text-gray-900">
                      {selectedAsset.purchaseDate ? new Date(selectedAsset.purchaseDate).toLocaleDateString() : 'N/A'}
                    </dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-sm font-medium text-gray-500">Purchase Cost</dt>
                    <dd className="text-sm text-gray-900">₹{selectedAsset.purchaseCost?.toLocaleString() || 'N/A'}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-sm font-medium text-gray-500">Warranty Expiry</dt>
                    <dd className="text-sm text-gray-900">
                      {selectedAsset.warrantyExpiry ? new Date(selectedAsset.warrantyExpiry).toLocaleDateString() : 'N/A'}
                    </dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-sm font-medium text-gray-500">Created</dt>
                    <dd className="text-sm text-gray-900">
                      {selectedAsset.createdAt ? new Date(selectedAsset.createdAt).toLocaleDateString() : 'N/A'}
                    </dd>
                  </div>
                </dl>
              </div>
            </div>

            {/* Maintenance History */}
            {selectedAsset.maintenanceHistory && selectedAsset.maintenanceHistory.length > 0 && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-gradient-to-r from-amber-500 to-orange-500 rounded-xl flex items-center justify-center">
                      <WrenchScrewdriverIcon className="h-4 w-4 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">Maintenance History</h3>
                      <p className="text-sm text-gray-600">{selectedAsset.maintenanceHistory.length} maintenance record{(selectedAsset.maintenanceHistory.length) !== 1 ? 's' : ''}</p>
                    </div>
                  </div>

                  {/* Quick Action Buttons if asset is under maintenance */}
                  {selectedAsset.currentStatus === 'under_maintenance' && (
                    <div className="flex space-x-2">
                      {selectedAsset.maintenanceHistory.find(m => m.status === 'scheduled') && (
                        <Button
                          onClick={() => {
                            setShowDetailsModal(false);
                            handleStartMaintenance(selectedAsset);
                          }}
                          className="px-3 py-1.5 text-sm bg-blue-600 hover:bg-blue-700"
                        >
                          Start Maintenance
                        </Button>
                      )}
                      {selectedAsset.maintenanceHistory.find(m => m.status === 'ongoing') && (
                        <>
                          <Button
                            onClick={() => {
                              setShowDetailsModal(false);
                              handleCompleteMaintenance(selectedAsset);
                            }}
                            className="px-3 py-1.5 text-sm bg-green-600 hover:bg-green-700"
                          >
                            Complete
                          </Button>
                          <Button
                            onClick={() => {
                              setShowDetailsModal(false);
                              handleCancelMaintenance(selectedAsset);
                            }}
                            className="px-3 py-1.5 text-sm bg-red-600 hover:bg-red-700"
                          >
                            Cancel
                          </Button>
                        </>
                      )}
                    </div>
                  )}
                </div>

                <div className="space-y-4">
                  {selectedAsset.maintenanceHistory.map((maintenance, index) => (
                    <div key={maintenance._id || index} className="bg-gradient-to-r from-gray-50 to-slate-50 rounded-xl p-4 border border-gray-200/50">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center space-x-3">
                          <div className={`w-3 h-3 rounded-full flex items-center justify-center ${
                            maintenance.status === 'scheduled' ? 'bg-yellow-400' :
                            maintenance.status === 'ongoing' ? 'bg-blue-500' :
                            maintenance.status === 'completed' ? 'bg-green-500' :
                            maintenance.status === 'cancelled' ? 'bg-red-500' : 'bg-gray-400'
                          }`}>
                            <div className={`w-1.5 h-1.5 rounded-full ${
                              maintenance.status === 'scheduled' ? 'bg-yellow-300' :
                              maintenance.status === 'ongoing' ? 'bg-blue-300' :
                              maintenance.status === 'completed' ? 'bg-green-300' :
                              maintenance.status === 'cancelled' ? 'bg-red-400' : 'bg-gray-300'
                            }`}></div>
                          </div>
                          <div>
                            <h4 className="font-semibold text-gray-900 capitalize">{maintenance.type} Maintenance</h4>
                            <p className="text-sm text-gray-600">
                              {maintenance.status === 'scheduled' ? 'Scheduled for ' :
                               maintenance.status === 'ongoing' ? 'Started on ' :
                               maintenance.status === 'completed' ? 'Completed on ' :
                               maintenance.status === 'cancelled' ? 'Cancelled on ' : 'Status: '}
                              {maintenance.startDate ? new Date(maintenance.startDate).toLocaleDateString() : 'N/A'}
                            </p>
                          </div>
                        </div>
                        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                          maintenance.status === 'scheduled' ? 'bg-yellow-100 text-yellow-800' :
                          maintenance.status === 'ongoing' ? 'bg-blue-100 text-blue-800' :
                          maintenance.status === 'completed' ? 'bg-green-100 text-green-800' :
                          maintenance.status === 'cancelled' ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {maintenance.status?.replace('_', ' ').toUpperCase()}
                        </span>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 text-sm">
                        {maintenance.description && (
                          <div className="space-y-1">
                            <span className="text-xs font-medium text-gray-500 uppercase">Description</span>
                            <p className="text-gray-900">{maintenance.description}</p>
                          </div>
                        )}
                        {maintenance.endDate && (
                          <div className="space-y-1">
                            <span className="text-xs font-medium text-gray-500 uppercase">
                              {maintenance.status === 'completed' ? 'Completed' : 'Cancelled'}
                            </span>
                            <p className="text-gray-900">{new Date(maintenance.endDate).toLocaleDateString()}</p>
                          </div>
                        )}
                        {maintenance.cost && (
                          <div className="space-y-1">
                            <span className="text-xs font-medium text-gray-500 uppercase">Cost</span>
                            <p className="text-gray-900">₹{maintenance.cost.toLocaleString()}</p>
                          </div>
                        )}
                        {maintenance.vendor && (
                          <div className="space-y-1">
                            <span className="text-xs font-medium text-gray-500 uppercase">Vendor</span>
                            <p className="text-gray-900">{maintenance.vendor}</p>
                          </div>
                        )}
                        {maintenance.technician && (
                          <div className="space-y-1">
                            <span className="text-xs font-medium text-gray-500 uppercase">Technician</span>
                            <p className="text-gray-900">{maintenance.technician}</p>
                          </div>
                        )}
                      </div>

                      <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-200">
                        <div className="text-xs text-gray-500">
                          Scheduled by {maintenance.scheduledBy?.firstName} {maintenance.scheduledBy?.lastName}
                          {maintenance.completedBy && maintenance.status === 'completed' && (
                            <> • Completed by {maintenance.completedBy.firstName} {maintenance.completedBy.lastName}</>
                          )}
                        </div>
                        <div className="text-xs text-gray-400">
                          {new Date(maintenance.scheduledBy ? Date.now() : maintenance.createdAt || Date.now()).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Asset Images Gallery */}
            {selectedAsset.images && selectedAsset.images.length > 0 && (
              <div className="space-y-6">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
                    <PhotoIcon className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">Asset Images</h3>
                    <p className="text-sm text-gray-600">{selectedAsset.images.length} image{(selectedAsset.images.length) !== 1 ? 's' : ''} available</p>
                  </div>
                </div>

                {/* Images Grid */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {selectedAsset.images.map((image, index) => (
                    <div key={index} className="group relative">
                      <div className="aspect-square bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-200 cursor-pointer transform hover:scale-[1.02]">
                        {image.fileId ? (
                          <AuthenticatedImage
                            fileId={image.fileId}
                            authToken={authToken}
                            alt={`Asset image ${index + 1}`}
                            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110 cursor-pointer"
                            onClick={async () => {
                              try {
                                const blobUrl = await getAuthenticatedImageUrl(image.fileId);
                                if (blobUrl) {
                                  setImagePreviewModal({ isOpen: true, image: blobUrl });
                                } else {
                                  toast.error('Failed to load image preview');
                                }
                              } catch (error) {
                                console.error('Error loading image preview:', error);
                                toast.error('Failed to load image preview');
                              }
                            }}
                          />
                        ) : (
                          // Fallback for legacy images (before GridFS)
                          <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-gray-200 to-gray-300 text-gray-400">
                            <svg className="w-8 h-8 mb-2 opacity-60" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd"/>
                            </svg>
                            <span className="text-xs font-medium">Legacy Image</span>
                          </div>
                        )}

                        {/* Image Hover Overlay - Clickable */}
                        <div
                          className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all duration-300 rounded-2xl flex items-center justify-center cursor-pointer"
                          onClick={async () => {
                            try {
                              const blobUrl = await getAuthenticatedImageUrl(image.fileId);
                              if (blobUrl) {
                                setImagePreviewModal({ isOpen: true, image: blobUrl });
                              } else {
                                toast.error('Failed to load image preview');
                              }
                            } catch (error) {
                              console.error('Error loading image preview:', error);
                              toast.error('Failed to load image preview');
                            }
                          }}
                        >
                          <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 p-2 text-white font-semibold">
                            <EyeIcon className="h-6 w-6 mx-auto mb-1" />
                            <span className="text-xs">Click to View</span>
                          </div>
                        </div>

                        {/* Image Number Badge */}
                        <div className="absolute top-2 right-2 bg-black bg-opacity-70 text-white text-xs px-2 py-1 rounded-full font-medium">
                          {index + 1}
                        </div>
                      </div>

                      {/* Image Info */}
                      <div className="mt-3 p-3 bg-gradient-to-r from-gray-50 to-white rounded-xl border border-gray-200">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                            <span className="text-xs font-medium text-gray-700">Image {index + 1}</span>
                          </div>
                          <span className="text-xs text-gray-500">
                            {(image.size ? (image.size / 1024).toFixed(1) : '0')} KB
                          </span>
                        </div>
                        <div className="text-xs text-gray-600 mt-1">
                          Click image to enlarge
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* No Images Placeholder */}
                {(!selectedAsset.images || selectedAsset.images.length === 0) && (
                  <div className="text-center py-12 bg-gradient-to-br from-gray-50 to-slate-50 rounded-2xl border-2 border-dashed border-gray-300">
                    <div className="w-16 h-16 bg-gradient-to-r from-gray-300 to-gray-400 rounded-full flex items-center justify-center mx-auto mb-4">
                      <PhotoIcon className="h-8 w-8 text-gray-600" />
                    </div>
                    <h4 className="text-lg font-bold text-gray-900 mb-2">No Images Available</h4>
                    <p className="text-gray-600 max-w-sm mx-auto">This asset doesn't have any images uploaded yet.</p>
                  </div>
                )}
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowDetailsModal(false);
                  setSelectedAsset(null);
                }}
              >
                Close
              </Button>
              {selectedAsset.currentStatus === 'available' && (
                <Button onClick={() => {
                  setShowDetailsModal(false);
                  handleAssignAsset(selectedAsset);
                }}>
                  Assign Asset
                </Button>
              )}
              {selectedAsset.currentStatus === 'assigned' && (
                <Button onClick={() => {
                  setShowDetailsModal(false);
                  handleReturnAsset(selectedAsset);
                }}>
                  Return Asset
                </Button>
              )}
            </div>
          </div>
        )}
      </Modal>

      {/* Request Details Modal */}
      <Modal
        isOpen={showRequestModal}
        onClose={() => {
          setShowRequestModal(false);
          setSelectedRequest(null);
        }}
        title="Request Details"
        size="md"
      >
        {selectedRequest && (
          <div className="space-y-4">
            <div className="flex items-center space-x-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <CubeIcon className="h-8 w-8 text-blue-600" />
              <div>
                <h3 className="font-medium text-gray-900">Request #{selectedRequest.requestNumber}</h3>
                <p className="text-sm text-gray-600">
                  {selectedRequest.employeeId?.firstName} {selectedRequest.employeeId?.lastName}
                </p>
              </div>
            </div>

            <dl className="space-y-3">
              <div className="flex justify-between">
                <dt className="text-sm font-medium text-gray-500">Requested Asset Type</dt>
                <dd className="text-sm text-gray-900">{categoryOptions.find(opt => opt.value === selectedRequest.assetType)?.label}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-sm font-medium text-gray-500">Priority</dt>
                <dd className="text-sm text-gray-900 capitalize">{selectedRequest.priority}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-sm font-medium text-gray-500">Status</dt>
                <dd className="text-sm text-gray-900 capitalize">{selectedRequest.status}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-sm font-medium text-gray-500">Created Date</dt>
                <dd className="text-sm text-gray-900">{new Date(selectedRequest.createdAt).toLocaleDateString()}</dd>
              </div>
            </dl>

            {selectedRequest.remarks && (
              <div>
                <dt className="text-sm font-medium text-gray-500 mb-1">Remarks</dt>
                <dd className="text-sm text-gray-900 bg-gray-50 p-3 rounded-md">{selectedRequest.remarks}</dd>
              </div>
            )}

            <div className="flex justify-end space-x-3 pt-4">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowRequestModal(false);
                  setSelectedRequest(null);
                }}
              >
                Close
              </Button>
              {selectedRequest.status === 'submitted' && (
                <>
                  <Button
                    onClick={() => {
                      handleApproveRequest(selectedRequest);
                      setShowRequestModal(false);
                    }}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    Approve
                  </Button>
                  <Button
                    onClick={() => {
                      handleRejectRequest(selectedRequest);
                      setShowRequestModal(false);
                    }}
                    className="bg-red-600 hover:bg-red-700"
                  >
                    Reject
                  </Button>
                </>
              )}
            </div>
          </div>
        )}
      </Modal>

      {/* Asset History/Timeline Modal */}
      <Modal
        isOpen={showHistoryModal}
        onClose={() => {
          setShowHistoryModal(false);
          setSelectedAsset(null);
        }}
        title="Asset Assignment History & Timeline"
        size="xl"
      >
        <div className="space-y-6">
          {/* Asset Info Header */}
          {selectedAsset && (
            <div className="flex items-center space-x-4 p-4 bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-lg">
              <div className="w-10 h-10 bg-purple-500 rounded-lg flex items-center justify-center">
                <ClockIcon className="h-5 w-5 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">{selectedAsset.assetName}</h3>
                <p className="text-sm text-gray-600">Asset ID: {selectedAsset.assetId} • Current Status: <span className="font-medium capitalize">{selectedAsset.currentStatus?.replace('_', ' ')}</span></p>
              </div>
            </div>
          )}

          {/* Timeline Section */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="text-lg font-semibold text-gray-900 flex items-center">
                <ClockIcon className="h-6 w-6 mr-3 text-purple-600" />
                Assignment Timeline
                <span className="ml-3 px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm font-medium">
                  {assetHistory?.data?.length || 0} event{(assetHistory?.data?.length || 0) !== 1 ? 's' : ''}
                </span>
              </h4>
            </div>

            {historyLoading ? (
              <div className="flex items-center justify-center py-12 space-x-3">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                <span className="text-gray-600 font-medium">Loading asset history...</span>
              </div>
            ) : !assetHistory?.data || assetHistory.data.length === 0 ? (
              <div className="text-center py-12 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl border-2 border-dashed border-gray-300">
                <ClockIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <h4 className="text-2xl font-bold text-gray-900 mb-3">No History Available</h4>
                <p className="text-gray-600 max-w-md mx-auto leading-relaxed text-lg">
                  This asset doesn't have any assignment or activity history yet. History will appear here once the asset is assigned, returned, or undergoes other activities.
                </p>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Current Assignment Info */}
                {selectedAsset?.currentHolder && (
                  <div className="bg-gradient-to-r from-green-50 to-emerald-100 rounded-2xl p-6 border-2 border-green-200">
                    <div className="flex items-center space-x-3 mb-4">
                      <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
                        <UserSolidIcon className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <h4 className="text-lg font-bold text-green-900">Currently Assigned</h4>
                        <p className="text-green-700">This asset is currently in use by an employee</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-white/60 rounded-lg p-3">
                        <p className="text-xs font-medium text-green-700 uppercase tracking-wide">Assigned To</p>
                        <p className="text-base font-bold text-green-900">
                          {selectedAsset.currentHolder.firstName} {selectedAsset.currentHolder.lastName}
                        </p>
                        <p className="text-sm text-green-600">{selectedAsset.currentHolder.employeeId}</p>
                      </div>
                      <div className="bg-white/60 rounded-lg p-3">
                        <p className="text-xs font-medium text-green-700 uppercase tracking-wide">Assignment Date</p>
                        <p className="text-base font-bold text-green-900">
                          {selectedAsset.currentAssignmentDate
                            ? new Date(selectedAsset.currentAssignmentDate).toLocaleDateString('en-US', {
                                weekday: 'short',
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric'
                              })
                            : 'Date not available'
                          }
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Main Timeline */}
                <div className="space-y-6">
                  {assetHistory.data.map((event, index) => (
                    <div key={event._id} className="relative">
                      {/* Timeline Line */}
                      <div className="absolute left-6 top-12 w-0.5 h-full bg-gradient-to-b from-purple-300 to-indigo-300">
                        {index < assetHistory.data.length - 1 && (
                          <div className="w-full h-full bg-gradient-to-b from-purple-300 to-indigo-300"></div>
                        )}
                      </div>

                      <div className="flex items-start space-x-6">
                        {/* Timeline Node */}
                        <div className={`relative w-12 h-12 rounded-full shadow-lg border-4 border-white flex items-center justify-center ${
                          event.eventType === 'assigned' ? 'bg-green-500' :
                          event.eventType === 'returned' ? 'bg-blue-500' :
                          event.eventType === 'transferred' ? 'bg-orange-500' :
                          event.eventType === 'created' ? 'bg-purple-500' :
                          event.eventType === 'updated' ? 'bg-indigo-500' :
                          'bg-gray-500'
                        }`}>
                          {getEventIcon(event.eventType)}
                          {index === 0 && (
                            <div className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center animate-pulse">
                              <div className="w-2 h-2 bg-white rounded-full"></div>
                            </div>
                          )}
                        </div>

                        {/* Event Card */}
                        <div className={`flex-1 bg-white rounded-2xl shadow-lg border-l-4 pb-6 ${
                          event.eventType === 'assigned' ? 'border-green-500' :
                          event.eventType === 'returned' ? 'border-blue-500' :
                          event.eventType === 'transferred' ? 'border-orange-500' :
                          event.eventType === 'created' ? 'border-purple-500' :
                          event.eventType === 'updated' ? 'border-indigo-500' :
                          'border-gray-300'
                        }`}>
                          <div className={`p-6 rounded-t-2xl ${
                            event.eventType === 'assigned' ? 'bg-gradient-to-r from-green-50 to-emerald-50' :
                            event.eventType === 'returned' ? 'bg-gradient-to-r from-blue-50 to-sky-50' :
                            event.eventType === 'transferred' ? 'bg-gradient-to-r from-orange-50 to-amber-50' :
                            event.eventType === 'created' ? 'bg-gradient-to-r from-purple-50 to-violet-50' :
                            event.eventType === 'updated' ? 'bg-gradient-to-r from-indigo-50 to-blue-50' :
                            'bg-gradient-to-r from-gray-50 to-slate-50'
                          }`}>
                            <div className="flex items-start justify-between mb-4">
                              <div>
                                <h5 className={`text-xl font-bold capitalize ${
                                  event.eventType === 'assigned' ? 'text-green-900' :
                                  event.eventType === 'returned' ? 'text-blue-900' :
                                  event.eventType === 'transferred' ? 'text-orange-900' :
                                  event.eventType === 'created' ? 'text-purple-900' :
                                  event.eventType === 'updated' ? 'text-indigo-900' :
                                  'text-gray-900'
                                }`}>
                                  {event.eventType === 'assigned' ? 'Asset Assigned' : ''}
                                  {event.eventType === 'returned' ? 'Asset Returned' : ''}
                                  {event.eventType === 'transferred' ? 'Asset Transferred' : ''}
                                  {event.eventType === 'created' ? 'Asset Created' : ''}
                                  {event.eventType === 'updated' ? 'Asset Updated' : ''}
                                  {!['assigned', 'returned', 'transferred', 'created', 'updated'].includes(event.eventType) && event.eventType.replace('_', ' ')}
                                </h5>
                                <p className="text-sm text-gray-700 leading-relaxed mt-1">{event.action || 'Asset management activity'}</p>
                              </div>
                              <div className="text-right">
                                <p className="text-sm font-bold text-gray-900">
                                  {new Date(event.performedAt).toLocaleDateString('en-US', {
                                    weekday: 'short',
                                    month: 'short',
                                    day: 'numeric',
                                    year: 'numeric'
                                  })}
                                </p>
                                <p className="text-xs text-gray-500 mt-1">
                                  {new Date(event.performedAt).toLocaleTimeString('en-US', {
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                </p>
                              </div>
                            </div>
                          </div>

                          {/* Event Details Content */}
                          <div className="p-6">
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                              {/* Left Column */}
                              <div className="space-y-4">
                                <div>
                                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Performed By</p>
                                  <div className="flex items-center space-x-3">
                                    <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full flex items-center justify-center">
                                      <UserSolidIcon className="h-4 w-4 text-white" />
                                    </div>
                                    <div>
                                      <p className="text-sm font-medium text-gray-900">
                                        {event.performedBy?.firstName} {event.performedBy?.lastName}
                                      </p>
                                      <p className="text-xs text-gray-500">
                                        {event.performedBy?.designation || 'Administrator'}
                                      </p>
                                    </div>
                                  </div>
                                </div>

                                {/* Assignment/Reassignment Details */}
                                {event.assignmentDetails && event.assignmentDetails.employeeId && (
                                  <div>
                                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                                      {event.eventType === 'returned' ? 'Returned From' :
                                       event.eventType === 'transferred' && event.assignmentDetails.fromEmployeeId ? 'Transferred From' :
                                       'Assigned To'}
                                    </p>
                                    <div className="flex items-center space-x-3">
                                      <div className="w-8 h-8 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center">
                                        <UserSolidIcon className="h-4 w-4 text-white" />
                                      </div>
                                      <div>
                                        <p className="text-sm font-medium text-gray-900">
                                          {event.assignmentDetails.employeeId.firstName} {event.assignmentDetails.employeeId.lastName}
                                        </p>
                                        <p className="text-xs text-gray-500">
                                          ID: {event.assignmentDetails.employeeId.employeeId}
                                        </p>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>

                              {/* Right Column */}
                              <div className="space-y-4">
                                {/* Transfer To Details */}
                                {event.assignmentDetails && event.assignmentDetails.toEmployeeId && (
                                  <div>
                                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Transferred To</p>
                                    <div className="flex items-center space-x-3">
                                      <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full flex items-center justify-center">
                                        <UserSolidIcon className="h-4 w-4 text-white" />
                                      </div>
                                      <div>
                                        <p className="text-sm font-medium text-gray-900">
                                          {event.assignmentDetails.toEmployeeId.firstName} {event.assignmentDetails.toEmployeeId.lastName}
                                        </p>
                                        <p className="text-xs text-gray-500">
                                          ID: {event.assignmentDetails.toEmployeeId.employeeId}
                                        </p>
                                      </div>
                                    </div>
                                  </div>
                                )}

                                {/* Asset Condition */}
                                {event.assignmentDetails && event.assignmentDetails.condition && (
                                  <div>
                                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Asset Condition</p>
                                    <span className={`inline-flex px-3 py-1 text-xs font-bold rounded-full capitalize ${
                                      event.assignmentDetails.condition === 'excellent' ? 'bg-green-100 text-green-800' :
                                      event.assignmentDetails.condition === 'good' ? 'bg-blue-100 text-blue-800' :
                                      event.assignmentDetails.condition === 'fair' ? 'bg-yellow-100 text-yellow-800' :
                                      'bg-red-100 text-red-800'
                                    }`}>
                                      {event.assignmentDetails.condition}
                                    </span>
                                  </div>
                                )}

                                {/* Branch Transfer */}
                                {event.assignmentDetails && (event.assignmentDetails.fromBranchId || event.assignmentDetails.toBranchId) && (
                                  <div>
                                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Branch Change</p>
                                    <div className="space-y-1">
                                      {event.assignmentDetails.fromBranchId && (
                                        <p className="text-xs text-red-600 bg-red-50 rounded px-2 py-1">
                                          From: {event.assignmentDetails.fromBranchId.name || event.assignmentDetails.fromBranchId.branchName}
                                        </p>
                                      )}
                                      {event.assignmentDetails.toBranchId && (
                                        <p className="text-xs text-green-600 bg-green-50 rounded px-2 py-1">
                                          To: {event.assignmentDetails.toBranchId.name || event.assignmentDetails.toBranchId.branchName}
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Notes/Remarks */}
                            {(event.assignmentDetails?.notes || event.assignmentDetails?.reason || event.remarks) && (
                              <div className="mt-4 p-4 bg-gray-50 rounded-lg border">
                                <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">Additional Details</p>
                                <div className="space-y-2">
                                  {event.assignmentDetails?.notes && (
                                    <div>
                                      <span className="text-xs font-medium text-gray-600">Notes: </span>
                                      <span className="text-sm text-gray-900">{event.assignmentDetails.notes}</span>
                                    </div>
                                  )}
                                  {event.assignmentDetails?.reason && (
                                    <div>
                                      <span className="text-xs font-medium text-gray-600">Reason: </span>
                                      <span className="text-sm text-gray-900">{event.assignmentDetails.reason}</span>
                                    </div>
                                  )}
                                  {event.remarks && (
                                    <div>
                                      <span className="text-xs font-medium text-gray-600">Remarks: </span>
                                      <span className="text-sm text-gray-900">{event.remarks}</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Modal Footer */}
          <div className="border-t border-gray-200 pt-6">
            <div className="flex justify-between items-center">
              <div className="text-sm text-gray-600">
                Tracking {assetHistory?.data?.length || 0} lifecycle events
              </div>
              <Button
                onClick={() => {
                  setShowHistoryModal(false);
                  setSelectedAsset(null);
                }}
                className="px-6 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors duration-200"
              >
                Close History
              </Button>
            </div>
          </div>
        </div>
      </Modal>

      {/* Edit Asset Modal */}
      <Modal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedAsset(null);
          setSelectedImages([]);
          setAssetForm({
            assetName: '',
            category: '',
            model: '',
            serialNumber: '',
            vendor: '',
            purchaseDate: '',
            purchaseCost: '',
            warrantyExpiry: '',
            branchId: ''
          });
        }}
        title="Edit Asset"
        size="lg"
      >
        {selectedAsset && (
          <div className="max-w-4xl mx-auto p-2">
            {/* Modal Header */}
            <div className="flex items-center justify-between mb-6 pb-3 border-b border-gray-200">
              <div className="flex items-center space-x-2">
                <div className="p-1.5 bg-indigo-100 rounded-md">
                  <PencilIcon className="h-5 w-5 text-indigo-600" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-gray-900">Edit Asset</h2>
                  <p className="text-xs text-gray-600">Update asset information and upload new images</p>
                  <p className="text-xs text-gray-500 mt-1">Asset ID: {selectedAsset.assetId}</p>
                </div>
              </div>
            </div>

            <form onSubmit={handleEditSubmit} className="space-y-6">
                  {/* Existing Images Section */}
                  {selectedAsset.images && selectedAsset.images.length > 0 && (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <h3 className="text-sm font-semibold text-gray-800 border-b border-gray-200 pb-2">Existing Images</h3>
                        <span className="text-xs text-gray-500">{selectedAsset.images.length} existing images</span>
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4 p-4 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                        {selectedAsset.images.map((image, index) => (
                          <div key={index} className="relative group">
                            <div className="aspect-square bg-white rounded-lg border-2 border-gray-200 overflow-hidden shadow-sm">
                              {image.fileId ? (
                                <AuthenticatedImage
                                  fileId={image.fileId}
                                  authToken={authToken}
                                  alt={`Asset image ${index + 1}`}
                                  className="w-full h-full object-cover cursor-pointer"
                                  onClick={async () => {
                                    try {
                                      const blobUrl = await getAuthenticatedImageUrl(image.fileId);
                                      if (blobUrl) {
                                        setImagePreviewModal({ isOpen: true, image: blobUrl });
                                      } else {
                                        toast.error('Failed to load image preview');
                                      }
                                    } catch (error) {
                                      console.error('Error loading image preview:', error);
                                      toast.error('Failed to load image preview');
                                    }
                                  }}
                                />
                              ) : (
                                // Fallback for legacy images (before GridFS)
                                <div className="w-full h-full flex flex-col items-center justify-center bg-gray-100 text-gray-400">
                                  <svg className="w-6 h-6 mb-2" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd"/>
                                  </svg>
                                  <span className="text-xs">Legacy Image</span>
                                </div>
                              )}
                            </div>
                            {/* Existing image indicator */}
                            <div className="mt-2 text-xs text-center text-gray-600">
                              Existing ({((image.size || 0) / 1024).toFixed(1)}KB)
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="text-xs text-gray-600 bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <p className="font-medium text-blue-800">Note: Existing images will remain unless replaced by new uploads.</p>
                      </div>
                    </div>
                  )}

              {/* Basic Information */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-gray-800 border-b border-gray-200 pb-2">Basic Information</h3>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {/* Asset Name */}
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <CubeIcon className="h-4 w-4 text-blue-600" />
                      <label className="text-sm font-semibold text-gray-800">
                        Asset Name <span className="text-red-500">*</span>
                      </label>
                    </div>
                    <Input
                      type="text"
                      value={assetForm.assetName}
                      onChange={(e) => setAssetForm({...assetForm, assetName: e.target.value})}
                      placeholder="Enter asset name"
                      required
                      className="text-sm py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white/70 backdrop-blur-sm transition-all duration-200 hover:border-gray-300"
                    />
                  </div>

                  {/* Category */}
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <CubeIcon className="h-4 w-4 text-green-600" />
                      <label className="text-sm font-semibold text-gray-800">
                        Category <span className="text-red-500">*</span>
                      </label>
                    </div>
                    <select
                      value={assetForm.category}
                      onChange={(e) => setAssetForm({...assetForm, category: e.target.value})}
                      required
                      className="w-full px-3 py-3 border-2 border-gray-200 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white/70 backdrop-blur-sm transition-all duration-200 hover:border-gray-300 text-sm"
                    >
                      <option value="">Select Category</option>
                      {categoryOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Model */}
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <ComputerDesktopIcon className="h-4 w-4 text-purple-600" />
                      <label className="text-sm font-semibold text-gray-800">Model</label>
                    </div>
                    <Input
                      type="text"
                      value={assetForm.model}
                      onChange={(e) => setAssetForm({...assetForm, model: e.target.value})}
                      placeholder="Enter model name"
                      className="text-sm py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white/70 backdrop-blur-sm transition-all duration-200 hover:border-gray-300"
                    />
                  </div>

                  {/* Serial Number */}
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <WrenchScrewdriverIcon className="h-4 w-4 text-indigo-600" />
                      <label className="text-sm font-semibold text-gray-800">Serial Number</label>
                    </div>
                    <Input
                      type="text"
                      value={assetForm.serialNumber}
                      onChange={(e) => setAssetForm({...assetForm, serialNumber: e.target.value})}
                      placeholder="Enter serial number"
                      className="text-sm py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white/70 backdrop-blur-sm transition-all duration-200 hover:border-gray-300"
                    />
                  </div>

                  {/* Vendor */}
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <BuildingOfficeIcon className="h-4 w-4 text-orange-600" />
                      <label className="text-sm font-semibold text-gray-800">Vendor</label>
                    </div>
                    <Input
                      type="text"
                      value={assetForm.vendor}
                      onChange={(e) => setAssetForm({...assetForm, vendor: e.target.value})}
                      placeholder="Enter vendor name"
                      className="text-sm py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent bg-white/70 backdrop-blur-sm transition-all duration-200 hover:border-gray-300"
                    />
                  </div>

                  {/* Branch */}
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <BuildingOfficeIcon className="h-4 w-4 text-teal-600" />
                      <label className="text-sm font-semibold text-gray-800">Branch</label>
                    </div>
                    <select
                      value={assetForm.branchId}
                      onChange={(e) => setAssetForm({...assetForm, branchId: e.target.value})}
                      className="w-full px-3 py-3 border-2 border-gray-200 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white/70 backdrop-blur-sm transition-all duration-200 hover:border-gray-300 text-sm"
                      disabled={branchesLoading}
                    >
                      <option value="">
                        {branchesLoading ? 'Loading branches...' : 'Select Branch'}
                      </option>
                      {branchesData?.data?.map((branch) => (
                        <option key={branch._id} value={branch._id}>
                          {branch.name || branch.branchName || `Branch ${branch._id.slice(-6)}`}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Financial Information */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-gray-800 border-b border-gray-200 pb-2">Financial Information</h3>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {/* Purchase Date */}
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <CalendarDaysIcon className="h-4 w-4 text-red-600" />
                      <label className="text-sm font-semibold text-gray-800">
                        Purchase Date <span className="text-red-500">*</span>
                      </label>
                    </div>
                    <Input
                      type="date"
                      value={assetForm.purchaseDate}
                      onChange={(e) => setAssetForm({...assetForm, purchaseDate: e.target.value})}
                      required
                      className="text-sm py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent bg-white/70 backdrop-blur-sm transition-all duration-200 hover:border-gray-300"
                    />
                  </div>

                  {/* Purchase Cost */}
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <CurrencyDollarIcon className="h-4 w-4 text-green-600" />
                      <label className="text-sm font-semibold text-gray-800">
                        Purchase Cost <span className="text-red-500">*</span>
                      </label>
                    </div>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <span className="text-gray-500 text-sm font-semibold">₹</span>
                      </div>
                      <Input
                        type="number"
                        value={assetForm.purchaseCost}
                        onChange={(e) => setAssetForm({...assetForm, purchaseCost: e.target.value})}
                        placeholder="Enter purchase cost"
                        required
                        className="pl-8 text-sm py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white/70 backdrop-blur-sm transition-all duration-200 hover:border-gray-300"
                      />
                    </div>
                  </div>

                  {/* Warranty Expiry */}
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <ClockIcon className="h-4 w-4 text-amber-600" />
                      <label className="text-sm font-semibold text-gray-800">Warranty Expiry</label>
                    </div>
                    <Input
                      type="date"
                      value={assetForm.warrantyExpiry}
                      onChange={(e) => setAssetForm({...assetForm, warrantyExpiry: e.target.value})}
                      className="text-sm py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent bg-white/70 backdrop-blur-sm transition-all duration-200 hover:border-gray-300"
                    />
                  </div>
                </div>
              </div>

              {/* Add New Images */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-gray-800 border-b border-gray-200 pb-2">Add New Images</h3>
                  <span className="text-xs text-gray-500">
                    Total: {(selectedAsset.images?.length || 0) + selectedImages.length}/10 images
                  </span>
                </div>

                {/* Image Upload Buttons */}
                <div className="flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={handleGallerySelect}
                    className="flex items-center space-x-2 px-4 py-3 bg-purple-50 hover:bg-purple-100 text-purple-700 border-2 border-purple-200 rounded-lg transition-all duration-200 hover:scale-105 shadow-sm hover:shadow-md"
                  >
                    <PhotoIcon className="h-5 w-5" />
                    <span className="text-sm font-medium">Gallery</span>
                  </button>
                  <button
                    type="button"
                    onClick={handleCameraSelect}
                    className="flex items-center space-x-2 px-4 py-3 bg-blue-50 hover:bg-blue-100 text-blue-700 border-2 border-blue-200 rounded-lg transition-all duration-200 hover:scale-105 shadow-sm hover:shadow-md"
                  >
                    <CameraIcon className="h-5 w-5" />
                    <span className="text-sm font-medium">Camera</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setSelectedImages([])}
                    disabled={selectedImages.length === 0}
                    className="flex items-center space-x-2 px-4 py-3 bg-red-50 hover:bg-red-100 disabled:bg-gray-50 text-red-700 disabled:text-gray-400 border-2 border-red-200 disabled:border-gray-200 rounded-lg transition-all duration-200 hover:scale-105 disabled:hover:scale-100 shadow-sm hover:shadow-md disabled:shadow-none"
                  >
                    <XMarkIcon className="h-5 w-5" />
                    <span className="text-sm font-medium">Clear New Images</span>
                  </button>
                </div>

                {/* Hidden File Inputs */}
                <input
                  ref={galleryInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={(e) => handleImageSelect(e.target.files)}
                />
                <input
                  ref={cameraInputRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  className="hidden"
                  onChange={(e) => handleImageSelect(e.target.files)}
                />

                {/* New Images Preview Grid */}
                {selectedImages.length > 0 && (
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4 p-4 bg-green-50 rounded-lg border-2 border-dashed border-green-300">
                    {selectedImages.map((image, index) => (
                      <div key={index} className="relative group">
                        <div
                          className="aspect-square bg-white rounded-lg border-2 border-gray-200 overflow-hidden shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer"
                          onClick={() => handleImagePreview(URL.createObjectURL(image))}
                        >
                          <img
                            src={URL.createObjectURL(image)}
                            alt={`New asset image ${index + 1}`}
                            className="w-full h-full object-cover"
                          />
                        </div>

                        {/* Remove Button */}
                        <button
                          type="button"
                          onClick={() => removeImage(index)}
                          className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center shadow-md opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                        >
                          <XMarkIcon className="h-3 w-3" />
                        </button>

                        {/* New image indicator */}
                        <div className="mt-2 text-xs text-center text-green-600 font-medium">
                          New ({(image.size / 1024).toFixed(1)}KB)
                        </div>
                      </div>
                    ))}

                    {/* Add More Placeholder */}
                    {(selectedAsset.images?.length || 0) + selectedImages.length < 10 && (
                      <button
                        type="button"
                        onClick={handleGallerySelect}
                        className="aspect-square border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center text-gray-400 hover:text-gray-600 hover:border-gray-400 transition-all duration-200 hover:bg-gray-50"
                      >
                        <PlusIcon className="h-6 w-6" />
                        <span className="text-xs mt-1">Add More</span>
                      </button>
                    )}
                  </div>
                )}

                {/* Upload Instructions */}
                <div className="text-xs text-gray-500 bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <div className="flex items-start space-x-2">
                    <DocumentIcon className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-blue-800 mb-1">Image Guidelines:</p>
                      <ul className="space-y-1 text-blue-700">
                        <li>• Maximum 4 images total per asset</li>
                        <li>• New images will be added to existing ones</li>
                        <li>• Supported formats: JPG, PNG, GIF, WebP</li>
                        <li>• Maximum file size: 2MB per image</li>
                        <li>• Click on images to preview full size</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              {/* Form Actions */}
              <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-gray-200">
                <Button
                  variant="secondary"
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedAsset(null);
                    setSelectedImages([]);
                    setAssetForm({
                      assetName: '',
                      category: '',
                      model: '',
                      serialNumber: '',
                      vendor: '',
                      purchaseDate: '',
                      purchaseCost: '',
                      warrantyExpiry: '',
                      branchId: ''
                    });
                  }}
                  className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transform hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-xl"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  loading={updating}
                  className="flex-1 px-4 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg hover:from-indigo-700 hover:to-purple-700 transform hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-xl"
                >
                  {updating ? 'Updating Asset...' : 'Update Asset'}
                </Button>
              </div>
            </form>
          </div>
        )}
      </Modal>

      {/* Schedule Maintenance Modal */}
      <Modal
        isOpen={showScheduleMaintenanceModal}
        onClose={() => setShowScheduleMaintenanceModal(false)}
        title="Schedule Maintenance"
        size="md"
      >
        <div className="space-y-4">
          <div className="flex items-center space-x-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <WrenchScrewdriverIcon className="h-8 w-8 text-amber-600" />
            <div>
              <h3 className="font-medium text-gray-900">{selectedAsset?.assetName}</h3>
              <p className="text-sm text-gray-600">Asset ID: {selectedAsset?.assetId}</p>
            </div>
          </div>

          <form onSubmit={handleScheduleMaintenanceSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Maintenance Type <span className="text-red-500">*</span>
              </label>
              <select
                value={maintenanceForm.type}
                onChange={(e) => setMaintenanceForm({...maintenanceForm, type: e.target.value})}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              >
                <option value="preventive">Preventive Maintenance</option>
                <option value="corrective">Corrective Maintenance</option>
                <option value="predictive">Predictive Maintenance</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description <span className="text-red-500">*</span>
              </label>
              <textarea
                value={maintenanceForm.description}
                onChange={(e) => setMaintenanceForm({...maintenanceForm, description: e.target.value})}
                rows={3}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                placeholder="Describe the maintenance work..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Scheduled Date <span className="text-red-500">*</span>
              </label>
              <input
                type="datetime-local"
                value={maintenanceForm.startDate}
                onChange={(e) => setMaintenanceForm({...maintenanceForm, startDate: e.target.value})}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Estimated Cost (₹)
              </label>
              <input
                type="number"
                value={maintenanceForm.cost}
                onChange={(e) => setMaintenanceForm({...maintenanceForm, cost: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                placeholder="Enter estimated cost"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Vendor/Service Provider
              </label>
              <input
                type="text"
                value={maintenanceForm.vendor}
                onChange={(e) => setMaintenanceForm({...maintenanceForm, vendor: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                placeholder="Enter vendor name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Technician (Optional)
              </label>
              <input
                type="text"
                value={maintenanceForm.technician}
                onChange={(e) => setMaintenanceForm({...maintenanceForm, technician: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                placeholder="Enter technician name"
              />
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <Button
                variant="secondary"
                onClick={() => setShowScheduleMaintenanceModal(false)}
              >
                Cancel
              </Button>
              <Button type="submit" className="bg-amber-600 hover:bg-amber-700">
                Schedule Maintenance
              </Button>
            </div>
          </form>
        </div>
      </Modal>

      {/* Start Maintenance Modal */}
      <Modal
        isOpen={showStartMaintenanceModal}
        onClose={() => setShowStartMaintenanceModal(false)}
        title="Start Maintenance"
        size="sm"
      >
        <div className="space-y-4">
          <div className="text-center">
            <PlayIcon className="h-12 w-12 text-blue-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900">Begin Maintenance Work</h3>
            <p className="text-sm text-gray-600">
              This will mark the maintenance for <strong>{selectedAsset?.assetName}</strong> as "in progress".
            </p>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
            <div className="flex items-center space-x-2">
              <ExclamationTriangleIcon className="h-4 w-4 text-blue-600" />
              <p className="text-sm text-blue-800">
                The asset will be unavailable for use during maintenance.
              </p>
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <Button
              variant="secondary"
              onClick={() => setShowStartMaintenanceModal(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleStartMaintenanceSubmit}
              className="bg-blue-600 hover:bg-blue-700"
            >
              Start Maintenance
            </Button>
          </div>
        </div>
      </Modal>

      {/* Complete Maintenance Modal */}
      <Modal
        isOpen={showCompleteMaintenanceModal}
        onClose={() => setShowCompleteMaintenanceModal(false)}
        title="Complete Maintenance"
        size="md"
      >
        <div className="space-y-4">
          <div className="flex items-center space-x-3 p-4 bg-green-50 border border-green-200 rounded-lg">
            <CheckCircleIcon className="h-8 w-8 text-green-600" />
            <div>
              <h3 className="font-medium text-gray-900">{selectedAsset?.assetName}</h3>
              <p className="text-sm text-gray-600">Asset ID: {selectedAsset?.assetId}</p>
            </div>
          </div>

          <form onSubmit={handleCompleteMaintenanceSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Completion Type <span className="text-red-500">*</span>
              </label>
              <select
                value={maintenanceForm.completionType}
                onChange={(e) => setMaintenanceForm({...maintenanceForm, completionType: e.target.value})}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                <option value="complete">Maintenance Completed</option>
                <option value="partial">Partially Completed</option>
                <option value="cancelled">Could Not Complete</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Work Performed <span className="text-red-500">*</span>
              </label>
              <textarea
                value={maintenanceForm.workPerformed}
                onChange={(e) => setMaintenanceForm({...maintenanceForm, workPerformed: e.target.value})}
                rows={4}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="Describe the work performed..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Actual Cost (₹)
              </label>
              <input
                type="number"
                value={maintenanceForm.actualCost}
                onChange={(e) => setMaintenanceForm({...maintenanceForm, actualCost: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="Enter actual cost"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Technician
              </label>
              <input
                type="text"
                value={maintenanceForm.technician}
                onChange={(e) => setMaintenanceForm({...maintenanceForm, technician: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="Enter technician name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Vendor
              </label>
              <input
                type="text"
                value={maintenanceForm.vendor}
                onChange={(e) => setMaintenanceForm({...maintenanceForm, vendor: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="Enter vendor name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Additional Notes
              </label>
              <textarea
                value={maintenanceForm.additionalNotes}
                onChange={(e) => setMaintenanceForm({...maintenanceForm, additionalNotes: e.target.value})}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="Any additional notes or observations..."
              />
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <Button
                variant="secondary"
                onClick={() => setShowCompleteMaintenanceModal(false)}
              >
                Cancel
              </Button>
              <Button type="submit" className="bg-green-600 hover:bg-green-700">
                Complete Maintenance
              </Button>
            </div>
          </form>
        </div>
      </Modal>

      {/* Cancel Maintenance Modal */}
      <Modal
        isOpen={showCancelMaintenanceModal}
        onClose={() => setShowCancelMaintenanceModal(false)}
        title="Cancel Maintenance"
        size="sm"
      >
        <div className="space-y-4">
          <div className="text-center">
            <XCircleIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900">Cancel Maintenance</h3>
            <p className="text-sm text-gray-600">
              This will cancel the ongoing maintenance for <strong>{selectedAsset?.assetName}</strong>.
            </p>
          </div>

          <form onSubmit={handleCancelMaintenanceSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Reason for Cancellation <span className="text-red-500">*</span>
              </label>
              <textarea
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                rows={3}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                placeholder="Provide reason for cancellation..."
              />
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
              <div className="flex items-center space-x-2">
                <ExclamationTriangleIcon className="h-4 w-4 text-yellow-600" />
                <p className="text-sm text-yellow-800">
                  This action cannot be undone. The asset will become available again.
                </p>
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <Button
                variant="secondary"
                onClick={() => setShowCancelMaintenanceModal(false)}
              >
                Keep Maintenance
              </Button>
              <Button type="submit" className="bg-red-600 hover:bg-red-700">
                Cancel Maintenance
              </Button>
            </div>
          </form>
        </div>
      </Modal>

      {/* Image Preview Modal */}
      <Modal
        isOpen={imagePreviewModal.isOpen}
        onClose={() => setImagePreviewModal({ isOpen: false, image: null })}
        title="Image Preview"
        size="lg"
      >
        <div className="flex items-center justify-center min-h-[60vh]">
          {imagePreviewModal.image && (
            <img
              src={imagePreviewModal.image}
              alt="Preview"
              className="max-w-full max-h-[50vh] object-contain rounded-lg shadow-lg"
            />
          )}
        </div>
      </Modal>
    </>
  );
};

export default AdminAssetManagement;
